// src/run-rules.ts
function runRules(ast, rules, options) {
  const out = [];
  const log = options?.logger?.error ?? ((msg, err) => {
    console.error(msg, err);
  });
  for (const rule of rules) {
    let result;
    try {
      result = rule(ast);
    } catch (err) {
      log("[vibeguard-local] rule threw and was skipped", err);
      continue;
    }
    if (result !== null) {
      out.push(result);
    }
  }
  return out;
}

// src/ast-walk.ts
var AST_WALK_MAX_DEPTH = 1e3;
function astWalk(root, visit) {
  const stack = [{ node: root, key: null, depth: 0 }];
  while (stack.length > 0) {
    const frame = stack.pop();
    if (!frame) break;
    const { node, key, depth } = frame;
    if (depth > AST_WALK_MAX_DEPTH) {
      console.warn(
        `[vibeguard-local] astWalk: traversal exceeded MAX_DEPTH=${AST_WALK_MAX_DEPTH}; halting`
      );
      return;
    }
    if (node === null || node === void 0) continue;
    if (typeof node !== "object") continue;
    const result = visit(node, key);
    if (result === "stop") return;
    if (result === "skip") continue;
    const obj = node;
    const keys = Object.keys(obj);
    for (let i = keys.length - 1; i >= 0; i--) {
      const k = keys[i];
      if (k === void 0) continue;
      const child = obj[k];
      if (Array.isArray(child)) {
        for (let j = child.length - 1; j >= 0; j--) {
          stack.push({ node: child[j], key: k, depth: depth + 1 });
        }
      } else if (child !== null && typeof child === "object") {
        stack.push({ node: child, key: k, depth: depth + 1 });
      }
    }
  }
}

// src/extract-tables.ts
function extractFromTables(stmt) {
  const tables = [];
  astWalk(stmt, (node, key) => {
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if ("RangeVar" in obj && isFromShapedKey(key)) {
      const rv = obj["RangeVar"];
      tables.push(buildFromTable(rv));
      return void 0;
    }
    if (key === "relation" && typeof obj["relname"] === "string") {
      tables.push(buildFromTable(obj));
      return void 0;
    }
    if ("RangeSubselect" in obj && isFromShapedKey(key)) {
      const rs = obj["RangeSubselect"];
      const entry = {
        name: "",
        isSubquery: true,
        ...typeof rs.alias?.aliasname === "string" ? { alias: rs.alias.aliasname } : {}
      };
      tables.push(entry);
      return void 0;
    }
    return void 0;
  });
  return tables;
}
var FROM_SHAPED_KEYS = /* @__PURE__ */ new Set([
  "fromClause",
  "usingClause",
  "larg",
  "rarg"
]);
function isFromShapedKey(key) {
  return FROM_SHAPED_KEYS.has(key);
}
function buildFromTable(rv) {
  return {
    name: typeof rv.relname === "string" ? rv.relname : "",
    ...typeof rv.schemaname === "string" ? { schema: rv.schemaname } : {},
    ...typeof rv.alias?.aliasname === "string" ? { alias: rv.alias.aliasname } : {}
  };
}

// src/fix-utils.ts
function maskStringLiterals(sql) {
  const out = sql.split("");
  const len = sql.length;
  let i = 0;
  const blank = (start, end) => {
    for (let k = start; k < end && k < len; k++) {
      if (out[k] !== "\n" && out[k] !== "\r") out[k] = " ";
    }
  };
  while (i < len) {
    const ch = sql[i];
    if (ch === "-" && sql[i + 1] === "-") {
      let j = i + 2;
      while (j < len && sql[j] !== "\n") j++;
      blank(i, j);
      i = j;
      continue;
    }
    if (ch === "/" && sql[i + 1] === "*") {
      let j = i + 2;
      while (j < len - 1 && !(sql[j] === "*" && sql[j + 1] === "/")) j++;
      const end = Math.min(j + 2, len);
      blank(i, end);
      i = end;
      continue;
    }
    if (ch === "'" || (ch === "E" || ch === "e") && sql[i + 1] === "'") {
      const start = ch === "'" ? i : i + 1;
      let j = start + 1;
      while (j < len) {
        if (sql[j] === "'") {
          if (sql[j + 1] === "'") {
            j += 2;
            continue;
          }
          j++;
          break;
        }
        j++;
      }
      blank(i, j);
      i = j;
      continue;
    }
    if (ch === "$") {
      let tagEnd = i + 1;
      while (tagEnd < len && /[A-Za-z0-9_]/.test(sql[tagEnd])) {
        tagEnd++;
      }
      if (tagEnd < len && sql[tagEnd] === "$") {
        const tag = sql.slice(i, tagEnd + 1);
        const closeIdx = sql.indexOf(tag, tagEnd + 1);
        if (closeIdx !== -1) {
          const end = closeIdx + tag.length;
          blank(i, end);
          i = end;
          continue;
        }
      }
    }
    if (ch === '"') {
      let j = i + 1;
      while (j < len) {
        if (sql[j] === '"') {
          if (sql[j + 1] === '"') {
            j += 2;
            continue;
          }
          j++;
          break;
        }
        j++;
      }
      blank(i, j);
      i = j;
      continue;
    }
    i++;
  }
  return out.join("");
}

// src/rules/find-stmt.ts
function findFromBearingStmt(ast) {
  let result = null;
  astWalk(ast, (node) => {
    if (result) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if ("SelectStmt" in obj) {
      const stmt = obj["SelectStmt"];
      const fc = stmt["fromClause"];
      result = {
        kind: "SelectStmt",
        fromClause: Array.isArray(fc) ? fc : [],
        whereClause: stmt["whereClause"],
        targetRelname: null
      };
      return "stop";
    }
    if ("UpdateStmt" in obj) {
      const stmt = obj["UpdateStmt"];
      const rel = stmt["relation"] ?? void 0;
      const fc = stmt["fromClause"];
      result = {
        kind: "UpdateStmt",
        fromClause: buildEffectiveFromList(rel, fc),
        whereClause: stmt["whereClause"],
        targetRelname: typeof rel?.relname === "string" ? rel.relname : null
      };
      return "stop";
    }
    if ("DeleteStmt" in obj) {
      const stmt = obj["DeleteStmt"];
      const rel = stmt["relation"] ?? void 0;
      const uc = stmt["usingClause"];
      result = {
        kind: "DeleteStmt",
        fromClause: buildEffectiveFromList(rel, uc),
        whereClause: stmt["whereClause"],
        targetRelname: typeof rel?.relname === "string" ? rel.relname : null
      };
      return "stop";
    }
    return void 0;
  });
  return result;
}
function buildEffectiveFromList(target, extras) {
  const out = [];
  if (target !== void 0) {
    out.push({ RangeVar: target });
  }
  if (Array.isArray(extras)) {
    for (const e of extras) out.push(e);
  }
  return out;
}

// src/rules/sql-001-cartesian.ts
var SQL_001 = (ast) => {
  const shape = findFromBearingStmt(ast);
  if (!shape) return null;
  const rawFromClause = shape.fromClause;
  if (rawFromClause.length === 0) return null;
  const tables = extractFromTables({ fromClause: rawFromClause });
  if (tables.length < 2) return null;
  let hasJoin = false;
  astWalk({ fromClause: rawFromClause }, (node) => {
    if (hasJoin) return "stop";
    if (node && typeof node === "object" && "JoinExpr" in node) {
      hasJoin = true;
      return "stop";
    }
    return void 0;
  });
  if (hasJoin) return null;
  const whereClause = shape.whereClause;
  if (whereClause && hasCrossTablePredicate(whereClause, tables)) {
    return null;
  }
  const tableNames = tables.map((t) => t.alias ?? (t.name === "" ? "<subquery>" : t.name)).join(", ");
  const detail = buildDetail(shape, tables.length, tableNames);
  const fix = buildFix(shape);
  const result = {
    code: "SQL-001",
    title: "Cartesian explosion risk",
    severity: "block",
    confidence: 95,
    detail,
    fix,
    threatCategories: ["denial-of-service"]
  };
  return result;
};
function buildDetail(shape, count, tableNames) {
  if (shape.kind === "SelectStmt") {
    return `${count} tables (${tableNames}) appear in FROM without a JOIN clause or cross-table WHERE predicate. The result row count is the product of every table's row count, which grows multiplicatively and can exhaust memory on production-sized data.`;
  }
  const target = shape.targetRelname ?? "<table>";
  const cluster = shape.kind === "UpdateStmt" ? "FROM" : "USING";
  const verb = shape.kind === "UpdateStmt" ? "UPDATE" : "DELETE";
  return `${verb} on \`${target}\` plus its ${cluster} clause references ${count} tables (${tableNames}) without a JOIN clause or cross-table WHERE predicate. The planner builds the cartesian product of every relation before applying the WHERE filter \u2014 N\xD7M rows materialize for the write, which can exhaust memory and serialize the lock on production-sized data.`;
}
function buildFix(shape) {
  if (shape.kind === "SelectStmt") {
    return "Add an explicit JOIN ... ON / USING clause that relates the tables, or add WHERE conditions that connect their rows. If the cross-product was intentional, use CROSS JOIN explicitly.";
  }
  const cluster = shape.kind === "UpdateStmt" ? "FROM" : "USING";
  return `Add a WHERE predicate that connects the target relation to the ${cluster} relation (e.g. \`t1.parent_id = t2.id\`). If the secondary relation is unused, remove it from the statement entirely. If the cartesian was deliberate, the statement is almost certainly wrong \u2014 review with an operator before running.`;
}
function hasCrossTablePredicate(whereClause, tables) {
  const identifiers = /* @__PURE__ */ new Set();
  for (const t of tables) {
    if (t.alias) identifiers.add(t.alias);
    else if (t.name) identifiers.add(t.name);
  }
  if (identifiers.size < 2) return false;
  let found = false;
  astWalk(whereClause, (node) => {
    if (found) return "stop";
    if (!node || typeof node !== "object") return void 0;
    if (!("A_Expr" in node)) return void 0;
    const aexpr = node.A_Expr;
    const lQual = getColumnQualifier(aexpr["lexpr"]);
    const rQual = getColumnQualifier(aexpr["rexpr"]);
    if (lQual !== null && rQual !== null && lQual !== rQual && identifiers.has(lQual) && identifiers.has(rQual)) {
      found = true;
      return "stop";
    }
    return void 0;
  });
  return found;
}
function getColumnQualifier(expr) {
  if (!expr || typeof expr !== "object") return null;
  if (!("ColumnRef" in expr)) return null;
  const cr = expr.ColumnRef;
  const fields = cr.fields;
  if (!Array.isArray(fields) || fields.length < 2) return null;
  const f0 = fields[0];
  if (!f0 || typeof f0 !== "object") return null;
  const sf = f0;
  return typeof sf.String?.sval === "string" ? sf.String.sval : null;
}
var SQL_001_FIX = {
  fix(ast, sql) {
    if (SQL_001(ast) === null) return null;
    const masked = maskStringLiterals(sql);
    const NOT_KEYWORD = "(?!(?:WHERE|ORDER|GROUP|LIMIT|OFFSET|JOIN|INNER|LEFT|RIGHT|FULL|OUTER|CROSS|NATURAL|USING|ON|HAVING|UNION|INTERSECT|EXCEPT|RETURNING|FETCH|FOR|WINDOW)\\b)";
    const tableShape = `\\w+(?:\\.\\w+)?(?:\\s+(?:AS\\s+)?${NOT_KEYWORD}\\w+)?`;
    const fromTablePattern = `(${tableShape})\\s*,\\s*(${tableShape})`;
    const re = new RegExp(`\\bFROM\\s+${fromTablePattern}`, "i");
    const m = masked.match(re);
    if (!m || m.index === void 0) return null;
    const fromKeyword = sql.slice(m.index, m.index + "FROM".length);
    const tail = sql.slice(m.index + m[0].length);
    const head = sql.slice(0, m.index);
    const t1 = m[1];
    const t2 = m[2];
    if (!t1 || !t2) return null;
    const replacement = `${fromKeyword} ${t1} JOIN ${t2} ON TRUE /* TODO(vibeguard SQL-001): replace TRUE with a real predicate */`;
    return head + replacement + tail;
  }
};

// src/rules/sql-002-self-join.ts
var SQL_002 = (ast) => {
  const shape = findFromBearingStmt(ast);
  if (!shape) return null;
  const rawFromClause = shape.fromClause;
  if (rawFromClause.length === 0) return null;
  const tables = extractFromTables({ fromClause: rawFromClause });
  const groups = /* @__PURE__ */ new Map();
  for (const t of tables) {
    if (!t.name || t.isSubquery) continue;
    const list = groups.get(t.name);
    if (list) list.push(t);
    else groups.set(t.name, [t]);
  }
  for (const [relname, group] of groups) {
    if (group.length < 2) continue;
    const identifiers = /* @__PURE__ */ new Set();
    for (const t of group) {
      identifiers.add(t.alias ?? t.name);
    }
    const predicates = collectComparisons(
      rawFromClause,
      shape.whereClause
    );
    let foundGood = false;
    let foundAnyRelevant = false;
    for (const pred of predicates) {
      const classification = classifyComparison(pred, identifiers);
      if (classification === "unrelated") continue;
      foundAnyRelevant = true;
      if (classification === "good") {
        foundGood = true;
        break;
      }
    }
    if (foundGood) continue;
    const aliasList = Array.from(identifiers).join(", ");
    const aliasOrTable = group[0]?.alias ?? relname;
    void aliasOrTable;
    const reasonClause = foundAnyRelevant ? "the predicates connecting them are tautological or compare the same column on both sides" : "no predicate connects them";
    const result = {
      code: "SQL-002",
      title: "Self-join without disambiguating predicate",
      severity: "warn",
      confidence: 75,
      detail: `Table \`${relname}\` appears ${group.length} times in FROM (aliases: ${aliasList}), but ${reasonClause}. The query may produce duplicated rows or a cartesian-shaped result for matching rows.`,
      fix: "Verify the join predicate connects DIFFERENT columns of the two table references (e.g. `parent.id = child.parent_id`). If you genuinely want all-pairs of rows from the same table, use `CROSS JOIN` to express that intent explicitly.",
      threatCategories: ["integrity"]
    };
    return result;
  }
  return null;
};
function collectComparisons(fromClause, whereClause) {
  const out = [];
  const visit = (node) => {
    astWalk(node, (n) => {
      if (!n || typeof n !== "object") return void 0;
      if ("A_Expr" in n) {
        out.push(
          n.A_Expr
        );
      }
      return void 0;
    });
  };
  visit({ fromClause });
  if (whereClause !== void 0) visit(whereClause);
  return out;
}
function classifyComparison(aexpr, identifiers) {
  const left = getColumnInfo(aexpr["lexpr"]);
  const right = getColumnInfo(aexpr["rexpr"]);
  const leftInGroup = left.alias !== null && identifiers.has(left.alias);
  const rightInGroup = right.alias !== null && identifiers.has(right.alias);
  if (!leftInGroup || !rightInGroup) return "unrelated";
  if (left.alias === right.alias) return "tautology";
  if (left.column !== null && left.column === right.column) return "same-column";
  return "good";
}
function getColumnInfo(expr) {
  if (!expr || typeof expr !== "object") return { alias: null, column: null };
  if (!("ColumnRef" in expr)) {
    return { alias: null, column: null };
  }
  const cr = expr.ColumnRef;
  const fields = cr.fields;
  if (!Array.isArray(fields) || fields.length === 0) {
    return { alias: null, column: null };
  }
  if (fields.length === 1) {
    return { alias: null, column: stringField(fields[0]) };
  }
  const colField = fields[fields.length - 1];
  const aliasField = fields[fields.length - 2];
  return {
    alias: stringField(aliasField),
    column: stringField(colField)
  };
}
function stringField(field) {
  if (!field || typeof field !== "object") return null;
  const f = field;
  if (f.A_Star) return "*";
  return typeof f.String?.sval === "string" ? f.String.sval : null;
}

// src/rules/sql-003-unbounded.ts
var SQL_003 = (ast) => {
  let target = null;
  astWalk(ast, (node) => {
    if (target) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if ("UpdateStmt" in obj) {
      const stmt = obj["UpdateStmt"];
      if (stmt["whereClause"] === void 0) {
        target = {
          stmtType: "UPDATE",
          tableName: extractRelationName(stmt["relation"])
        };
        return "stop";
      }
      return void 0;
    }
    if ("DeleteStmt" in obj) {
      const stmt = obj["DeleteStmt"];
      if (stmt["whereClause"] === void 0) {
        target = {
          stmtType: "DELETE",
          tableName: extractRelationName(stmt["relation"])
        };
        return "stop";
      }
      return void 0;
    }
    return void 0;
  });
  if (!target) return null;
  const t = target;
  const verb = t.stmtType.toLowerCase();
  const verbAffected = t.stmtType === "UPDATE" ? "modified" : "deleted";
  const result = {
    code: "SQL-003",
    title: `Unbounded ${t.stmtType} statement`,
    severity: "block",
    confidence: t.stmtType === "UPDATE" ? 99 : 97,
    detail: `${t.stmtType} on \`${t.tableName}\` has no WHERE clause. Every row in the table will be ${verbAffected}. This is almost never the intended behavior for an AI-generated query against production data.`,
    fix: `Add a WHERE clause that scopes the ${verb} to specific rows. If you genuinely intend to ${verb} every row, ${t.stmtType === "DELETE" ? "use TRUNCATE or " : ""}run the statement under explicit operator review.`,
    threatCategories: ["destruction"]
  };
  return result;
};
function extractRelationName(relation) {
  if (!relation || typeof relation !== "object") return "<unknown>";
  const r = relation;
  const schema = typeof r.schemaname === "string" ? `${r.schemaname}.` : "";
  const name = typeof r.relname === "string" ? r.relname : "<unknown>";
  return `${schema}${name}`;
}

// src/rules/sql-004-coercion.ts
var NUMERIC_NAMES = /* @__PURE__ */ new Set([
  "id",
  "count",
  "total",
  "amount",
  "size",
  "age",
  "qty",
  "num",
  "price",
  "rank",
  "index",
  "version"
]);
var NUMERIC_SUFFIXES = [
  "_id",
  "_count",
  "_total",
  "_amount",
  "_size",
  "_age",
  "_qty",
  "_num",
  "_price",
  "_rank",
  "_index",
  "_version"
];
var TEXT_NAMES = /* @__PURE__ */ new Set([
  "name",
  "email",
  "phone",
  "url",
  "uri",
  "description",
  "title",
  "label",
  "tag",
  "slug",
  "path",
  "comment",
  "message"
]);
var TEXT_SUFFIXES = [
  "_name",
  "_email",
  "_phone",
  "_url",
  "_uri",
  "_text",
  "_description",
  "_title",
  "_label",
  "_tag",
  "_slug",
  "_path",
  "_comment",
  "_message"
];
var COMPARISON_OPS = /* @__PURE__ */ new Set(["=", "<", ">", "<=", ">=", "<>"]);
var SQL_004 = (ast) => {
  let hit = null;
  astWalk(ast, (node) => {
    if (hit) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("A_Expr" in obj)) return void 0;
    const aexpr = obj["A_Expr"];
    if (aexpr["kind"] !== "AEXPR_OP") return void 0;
    const op = readOperator(aexpr["name"]);
    if (op === null || !COMPARISON_OPS.has(op)) return void 0;
    const left = aexpr["lexpr"];
    const right = aexpr["rexpr"];
    const pair = findColRefConstPair(left, right);
    if (!pair) return void 0;
    const colHint = classifyColumn(pair.columnName);
    if (colHint === null) return void 0;
    const litKind = classifyLiteral(pair.literal);
    if (litKind === null) return void 0;
    if (colHint === "numeric" && litKind === "string") {
      hit = {
        columnName: pair.columnName,
        columnType: "numeric",
        literalKind: "string"
      };
      return "stop";
    }
    if (colHint === "text" && (litKind === "integer" || litKind === "float")) {
      hit = {
        columnName: pair.columnName,
        columnType: "text",
        literalKind: litKind
      };
      return "stop";
    }
    return void 0;
  });
  if (!hit) return null;
  const h = hit;
  const result = {
    code: "SQL-004",
    title: "Likely implicit type coercion in comparison",
    severity: "warn",
    confidence: 75,
    detail: `Column \`${h.columnName}\` is compared against a ${h.literalKind} literal, but its name strongly suggests a ${h.columnType} type. Postgres will attempt an implicit coercion (often disabling index use), or fail at runtime if the value can't be coerced.`,
    fix: "If the column type is what the name suggests, change the literal to match (or use an explicit cast like `'123'::int`). If the column actually holds the literal's type, the column name is misleading \u2014 consider renaming for future readers.",
    threatCategories: ["corruption"]
  };
  return result;
};
function readOperator(name) {
  if (!Array.isArray(name) || name.length === 0) return null;
  const first = name[0];
  if (!first || typeof first !== "object") return null;
  const f = first;
  return typeof f.String?.sval === "string" ? f.String.sval : null;
}
function findColRefConstPair(lexpr, rexpr) {
  const leftCol = readColumnName(lexpr);
  if (leftCol !== null && isBareConst(rexpr)) {
    return { columnName: leftCol, literal: rexpr };
  }
  const rightCol = readColumnName(rexpr);
  if (rightCol !== null && isBareConst(lexpr)) {
    return { columnName: rightCol, literal: lexpr };
  }
  return null;
}
function readColumnName(expr) {
  if (!expr || typeof expr !== "object") return null;
  if (!("ColumnRef" in expr)) return null;
  const cr = expr.ColumnRef;
  const fields = cr.fields;
  if (!Array.isArray(fields) || fields.length === 0) return null;
  const last = fields[fields.length - 1];
  if (!last || typeof last !== "object") return null;
  const f = last;
  if (f.A_Star) return null;
  return typeof f.String?.sval === "string" ? f.String.sval : null;
}
function isBareConst(expr) {
  if (!expr || typeof expr !== "object") return false;
  if ("TypeCast" in expr) return false;
  if (!("A_Const" in expr)) return false;
  return true;
}
function classifyColumn(name) {
  const lower = name.toLowerCase();
  if (NUMERIC_NAMES.has(lower)) return "numeric";
  if (TEXT_NAMES.has(lower)) return "text";
  for (const suffix of NUMERIC_SUFFIXES) {
    if (lower.endsWith(suffix)) return "numeric";
  }
  for (const suffix of TEXT_SUFFIXES) {
    if (lower.endsWith(suffix)) return "text";
  }
  return null;
}
function classifyLiteral(node) {
  if (!node || typeof node !== "object") return null;
  const wrap = node;
  const c = wrap.A_Const;
  if (!c) return null;
  if (c["isnull"] === true) return null;
  if ("sval" in c) return "string";
  if ("ival" in c) return "integer";
  if ("fval" in c) return "float";
  return null;
}

// src/rules/sql-005-null-comparison.ts
var SQL_005 = (ast) => {
  let firstHit = null;
  astWalk(ast, (node) => {
    if (firstHit) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("A_Expr" in obj)) return void 0;
    const aexpr = obj["A_Expr"];
    if (aexpr["kind"] !== "AEXPR_OP") return void 0;
    const op = readOperatorName(aexpr["name"]);
    if (op !== "=" && op !== "<>") return void 0;
    if (isNullLiteral(aexpr["lexpr"])) {
      firstHit = { side: "left" };
      return "stop";
    }
    if (isNullLiteral(aexpr["rexpr"])) {
      firstHit = { side: "right" };
      return "stop";
    }
    return void 0;
  });
  if (!firstHit) return null;
  const result = {
    code: "SQL-005",
    title: "NULL comparison with `=` / `<>`",
    severity: "warn",
    confidence: 95,
    detail: "A comparison uses `=` or `<>` with the NULL literal. In SQL's three-valued logic this expression always evaluates to UNKNOWN (treated as false), so the WHERE / HAVING filter silently returns no matches \u2014 even for rows where the column genuinely is NULL. Almost always a bug; the LLM likely meant `IS NULL` / `IS NOT NULL`.",
    fix: "Replace `col = NULL` with `col IS NULL` (or `col IS NOT NULL` for the inverse). For NULL-safe equality across two values, use `IS DISTINCT FROM` / `IS NOT DISTINCT FROM`.",
    threatCategories: ["corruption"]
  };
  return result;
};
function readOperatorName(name) {
  if (!Array.isArray(name) || name.length === 0) return null;
  const first = name[0];
  if (!first || typeof first !== "object") return null;
  const f = first;
  return typeof f.String?.sval === "string" ? f.String.sval : null;
}
function isNullLiteral(operand) {
  if (!operand || typeof operand !== "object") return false;
  const wrap = operand;
  return wrap.A_Const?.isnull === true;
}
var SQL_005_FIX = {
  fix(ast, sql) {
    if (SQL_005(ast) === null) return null;
    const masked = maskStringLiterals(sql);
    const reEq = /(\b\w+(?:\.\w+)?)\s*=\s*NULL\b/i;
    const reNe = /(\b\w+(?:\.\w+)?)\s*(?:<>|!=)\s*NULL\b/i;
    const eq = masked.match(reEq);
    const ne = masked.match(reNe);
    let chosen = null;
    if (eq && eq.index !== void 0) {
      const ident = sql.slice(eq.index, eq.index + (eq[1] ?? "").length);
      chosen = { match: eq, replacement: `${ident} IS NULL` };
    }
    if (ne && ne.index !== void 0 && (chosen === null || ne.index < (chosen.match.index ?? 0))) {
      const ident = sql.slice(ne.index, ne.index + (ne[1] ?? "").length);
      chosen = { match: ne, replacement: `${ident} IS NOT NULL` };
    }
    if (!chosen || chosen.match.index === void 0) return null;
    const start = chosen.match.index;
    const end = start + chosen.match[0].length;
    return sql.slice(0, start) + chosen.replacement + sql.slice(end);
  }
};

// src/rules/sql-006-offset-without-orderby.ts
var SQL_006 = (ast) => {
  let selectStmt = null;
  astWalk(ast, (node) => {
    if (selectStmt) return "stop";
    if (node && typeof node === "object" && "SelectStmt" in node) {
      selectStmt = node.SelectStmt;
      return "stop";
    }
    return void 0;
  });
  if (!selectStmt) return null;
  const stmt = selectStmt;
  const offset = stmt["limitOffset"];
  if (offset === void 0 || offset === null) return null;
  if (isLiteralZero(offset)) return null;
  if (isLiteralNull(offset)) return null;
  const sortClause = stmt["sortClause"];
  if (Array.isArray(sortClause) && sortClause.length > 0) return null;
  const result = {
    code: "SQL-006",
    title: "OFFSET without ORDER BY",
    severity: "warn",
    confidence: 90,
    detail: "The query uses `OFFSET` without `ORDER BY`. Postgres does not guarantee row order without an explicit sort, so `OFFSET N` skips an arbitrary N rows. Pagination built on this pattern silently overlaps or skips results between pages \u2014 a bug that often only surfaces under load or after data growth.",
    fix: "Add an `ORDER BY` clause that produces a deterministic ordering (usually a unique key like `id` or `(created_at, id)`). If you genuinely want to skip an arbitrary subset of rows, the OFFSET is misleading \u2014 describe the intent in a comment so future readers know the unordered behavior is intentional.",
    threatCategories: ["integrity"]
  };
  return result;
};
function isLiteralZero(operand) {
  if (!operand || typeof operand !== "object") return false;
  const wrap = operand;
  const c = wrap.A_Const;
  if (!c) return false;
  if (c.isnull) return false;
  if (c.ival !== void 0) {
    const inner = c.ival.ival;
    return inner === void 0 || inner === 0;
  }
  return false;
}
function isLiteralNull(operand) {
  if (!operand || typeof operand !== "object") return false;
  const wrap = operand;
  return wrap.A_Const?.isnull === true;
}
var SQL_006_FIX = {
  fix(ast, sql) {
    if (SQL_006(ast) === null) return null;
    const masked = maskStringLiterals(sql);
    const limit = masked.match(/\bLIMIT\b/i);
    const offset = masked.match(/\bOFFSET\b/i);
    let pos = -1;
    if (limit && limit.index !== void 0) pos = limit.index;
    if (offset && offset.index !== void 0 && (pos === -1 || offset.index < pos)) {
      pos = offset.index;
    }
    if (pos === -1) return null;
    return `${sql.slice(0, pos)}ORDER BY 1 ${sql.slice(pos)}`;
  }
};

// src/rules/sql-007-not-in-nullable.ts
var NULL_GUARD_FUNCS = /* @__PURE__ */ new Set([
  "coalesce",
  "nullif",
  "ifnull",
  "isnull"
]);
var SQL_007 = (ast) => {
  let hit = null;
  astWalk(ast, (node) => {
    if (hit) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("BoolExpr" in obj)) return void 0;
    const be = obj["BoolExpr"];
    if (be.boolop !== "NOT_EXPR") return void 0;
    if (!Array.isArray(be.args)) return void 0;
    for (const arg of be.args) {
      if (!arg || typeof arg !== "object") continue;
      if (!("SubLink" in arg)) continue;
      const sl = arg.SubLink;
      if (sl.subLinkType !== "ANY_SUBLINK") continue;
      const subselect = sl.subselect;
      if (!subselect || typeof subselect !== "object") continue;
      const inner = subselect.SelectStmt;
      if (!inner) continue;
      const targetList = inner["targetList"];
      if (!Array.isArray(targetList) || targetList.length === 0) continue;
      const firstTarget = targetList[0];
      const proj = readProjectionExpr(firstTarget);
      if (!proj) continue;
      if (proj.kind === "guarded-by-function") continue;
      const colName = proj.column;
      const whereClause = inner["whereClause"];
      if (whereClause && hasIsNotNullGuard(whereClause, colName)) {
        continue;
      }
      hit = { columnName: colName };
      return "stop";
    }
    return void 0;
  });
  if (!hit) return null;
  const h = hit;
  const colDisplay = h.columnName ?? "<column>";
  const result = {
    code: "SQL-007",
    title: "NOT IN with potentially-nullable subquery",
    severity: "warn",
    confidence: 80,
    detail: `\`NOT IN (SELECT ${colDisplay} FROM ...)\` does not guard the subquery projection against NULL. If any returned row has NULL in that column, SQL's three-valued logic makes the entire NOT IN evaluate to UNKNOWN \u2014 and the outer WHERE silently filters every row out, even rows that are clearly not in the banned set.`,
    fix: `Add an \`IS NOT NULL\` predicate inside the subquery (\`WHERE ${colDisplay} IS NOT NULL\`), or wrap the projection in \`coalesce(${colDisplay}, <sentinel>)\`. Even better: rewrite as \`NOT EXISTS (SELECT 1 FROM ... WHERE outer.x = inner.${colDisplay})\` which is NULL-safe by construction.`,
    threatCategories: ["corruption"]
  };
  return result;
};
function readProjectionExpr(target) {
  if (!target || typeof target !== "object") return null;
  if (!("ResTarget" in target)) return null;
  const rt = target.ResTarget;
  const val = rt.val;
  if (!val || typeof val !== "object") return null;
  const obj = val;
  if ("CoalesceExpr" in obj) {
    return { kind: "guarded-by-function", column: null };
  }
  if ("A_Expr" in obj) {
    const ae = obj["A_Expr"];
    if (ae.kind === "AEXPR_NULLIF") {
      return { kind: "guarded-by-function", column: null };
    }
  }
  if ("FuncCall" in obj) {
    const fc = obj["FuncCall"];
    const fname = readLastFuncName(fc.funcname);
    if (fname && NULL_GUARD_FUNCS.has(fname)) {
      return { kind: "guarded-by-function", column: null };
    }
    return { kind: "column", column: null };
  }
  if ("ColumnRef" in obj) {
    const cr = obj["ColumnRef"];
    return { kind: "column", column: readColumnLastField(cr.fields) };
  }
  return { kind: "column", column: null };
}
function readLastFuncName(funcname) {
  if (!Array.isArray(funcname) || funcname.length === 0) return null;
  const last = funcname[funcname.length - 1];
  if (!last || typeof last !== "object") return null;
  const f = last;
  return typeof f.String?.sval === "string" ? f.String.sval.toLowerCase() : null;
}
function readColumnLastField(fields) {
  if (!Array.isArray(fields) || fields.length === 0) return null;
  const last = fields[fields.length - 1];
  if (!last || typeof last !== "object") return null;
  const f = last;
  if (f.A_Star) return null;
  return typeof f.String?.sval === "string" ? f.String.sval : null;
}
function hasIsNotNullGuard(whereClause, column) {
  if (!whereClause) return false;
  let found = false;
  astWalk(whereClause, (node) => {
    if (found) return "stop";
    if (!node || typeof node !== "object") return void 0;
    if (!("NullTest" in node)) return void 0;
    const nt = node.NullTest;
    if (nt.nulltesttype !== "IS_NOT_NULL") return void 0;
    if (column === null) {
      found = true;
      return "stop";
    }
    const arg = nt.arg;
    if (arg && typeof arg === "object" && "ColumnRef" in arg) {
      const cr = arg.ColumnRef;
      const argName = readColumnLastField(cr.fields);
      if (argName === column) {
        found = true;
        return "stop";
      }
    }
    return void 0;
  });
  return found;
}

// src/rules/sql-008-string-concat.ts
var PAYLOAD_SIGNATURE = /\b(OR|UNION|DROP|TRUNCATE|DELETE|EXEC|EXECUTE)\b|--|;\s*\w|\b1\s*=\s*1\b|'\s*=\s*'/i;
var SQL_008 = (ast) => {
  let hit = null;
  astWalk(ast, (node) => {
    if (hit) return "stop";
    if (!isStringConcat(node)) return void 0;
    const operands = flatten(node);
    const kinds = operands.map(classifyOperand);
    if (kinds.some((k) => k === "array")) return "skip";
    if (kinds.some((k) => k === "param")) {
      hit = {
        tier: "runtime-param",
        confidence: 90,
        severity: "block",
        hasParam: true
      };
      return "stop";
    }
    if (kinds.every((k) => k === "literal")) {
      const blob = operands.map(literalText).join(" ");
      if (PAYLOAD_SIGNATURE.test(blob)) {
        hit = {
          tier: "payload-shape",
          confidence: 75,
          severity: "info",
          hasParam: false
        };
        return "stop";
      }
      return "skip";
    }
    if (kinds.every((k) => k === "literal" || k === "column")) {
      return "skip";
    }
    hit = {
      tier: "runtime-mixed",
      confidence: 80,
      severity: "block",
      hasParam: false
    };
    return "stop";
  });
  if (!hit) return null;
  const h = hit;
  const detailLead = h.tier === "payload-shape" ? "A string-concatenation operator (`||`) combines literal strings, and at least one of those literals contains an injection-payload signature (e.g. `OR`, `1=1`, `--`, `;`). The query as written is constant-folded by the parser and is not exploitable at runtime, but the shape is the unmistakable footprint of injection-style code authoring \u2014 worth a manual review of how the surrounding code came to produce this query." : h.hasParam ? "A string-concatenation operator (`||`) combines a parameter with other operands. This is the canonical shape of SQL injection \u2014 even when the parameter is driver-bound, the concatenation result may be interpreted as SQL text rather than as a single value." : "A string-concatenation operator (`||`) combines a function result or other non-literal expression with other operands. Building SQL fragments via concatenation is a frequent source of injection vulnerabilities.";
  const result = {
    code: "SQL-008",
    title: "Possible string-concatenation injection",
    severity: h.severity,
    confidence: h.confidence,
    detail: `${detailLead} The LLM (or human) almost always meant to use parameterized values instead.`,
    fix: "Use parameterized queries: pass user-controlled values as `$1`, `$2`, ... bind parameters that the driver substitutes safely. For LIKE patterns, the safer form is `LIKE $1` where the caller passes `value%`; for dynamic identifier construction, use a vetted whitelist instead of concatenation.",
    threatCategories: ["injection"]
  };
  return result;
};
function readOperator2(name) {
  if (!Array.isArray(name) || name.length === 0) return null;
  const first = name[0];
  if (!first || typeof first !== "object") return null;
  const f = first;
  return typeof f.String?.sval === "string" ? f.String.sval : null;
}
function isStringConcat(node) {
  if (!node || typeof node !== "object") return false;
  if (!("A_Expr" in node)) return false;
  const a = node.A_Expr;
  if (a["kind"] !== "AEXPR_OP") return false;
  return readOperator2(a["name"]) === "||";
}
function flatten(node) {
  if (!isStringConcat(node)) return [node];
  const a = node.A_Expr;
  return [...flatten(a.lexpr), ...flatten(a.rexpr)];
}
function classifyOperand(node) {
  if (!node || typeof node !== "object") return "other";
  const obj = node;
  if ("A_Const" in obj) return "literal";
  if ("ParamRef" in obj) return "param";
  if ("ColumnRef" in obj) return "column";
  if ("A_ArrayExpr" in obj) return "array";
  return "other";
}
function literalText(node) {
  if (!node || typeof node !== "object") return "";
  const obj = node;
  return obj.A_Const?.sval?.sval ?? "";
}

// src/rules/sql-009-distinct.ts
var SQL_009 = (ast) => {
  let selectStmt = null;
  astWalk(ast, (node) => {
    if (selectStmt) return "stop";
    if (node && typeof node === "object" && "SelectStmt" in node) {
      selectStmt = node.SelectStmt;
      return "stop";
    }
    return void 0;
  });
  if (!selectStmt) return null;
  const stmt = selectStmt;
  const distinctClause = stmt["distinctClause"];
  if (!Array.isArray(distinctClause) || distinctClause.length === 0) {
    return null;
  }
  if (!isPlainDistinct(distinctClause)) return null;
  const targetList = stmt["targetList"];
  if (!Array.isArray(targetList) || targetList.length === 0) {
    return null;
  }
  if (!targetList.every(isStarTarget)) return null;
  const result = {
    code: "SQL-009",
    title: "DISTINCT applied to a star projection",
    severity: "info",
    confidence: 65,
    detail: "The query uses `DISTINCT *` (every column from every joined table). DISTINCT in this position rarely reduces row count \u2014 it compares full rows, and full-row duplicates are uncommon \u2014 so the LLM may have meant to dedupe on a narrower projection instead.",
    fix: "Either drop `DISTINCT` if the underlying query already returns unique rows, or specify which columns to dedupe on. For Postgres-specific keep-one-per-group behavior, use `SELECT DISTINCT ON (key) ...` with an `ORDER BY` that picks the right row per group.",
    threatCategories: ["integrity"]
  };
  return result;
};
function isPlainDistinct(clause) {
  if (clause.length !== 1) return false;
  const first = clause[0];
  if (!first || typeof first !== "object") return false;
  return Object.keys(first).length === 0;
}
function isStarTarget(target) {
  if (!target || typeof target !== "object") return false;
  if (!("ResTarget" in target)) return false;
  const rt = target.ResTarget;
  const val = rt.val;
  if (!val || typeof val !== "object") return false;
  if (!("ColumnRef" in val)) return false;
  const cr = val.ColumnRef;
  const fields = cr.fields;
  if (!Array.isArray(fields) || fields.length === 0) return false;
  const last = fields[fields.length - 1];
  if (!last || typeof last !== "object") return false;
  return "A_Star" in last;
}

// src/rules/sql-010-correlated-subquery.ts
var SQL_010 = (ast) => {
  let selectStmt = null;
  astWalk(ast, (node) => {
    if (selectStmt) return "stop";
    if (node && typeof node === "object" && "SelectStmt" in node) {
      selectStmt = node.SelectStmt;
      return "stop";
    }
    return void 0;
  });
  if (!selectStmt) return null;
  const stmt = selectStmt;
  const targetList = stmt["targetList"];
  if (!Array.isArray(targetList) || targetList.length === 0) return null;
  const rawFromClause = stmt["fromClause"];
  if (!Array.isArray(rawFromClause) || rawFromClause.length === 0) {
    return null;
  }
  const outerTables = extractFromTables({ fromClause: rawFromClause });
  const outerIds = /* @__PURE__ */ new Set();
  for (const t of outerTables) {
    if (t.alias) outerIds.add(t.alias);
    else if (t.name) outerIds.add(t.name);
  }
  if (outerIds.size === 0) return null;
  let hit = null;
  for (const target of targetList) {
    if (hit) break;
    if (!target || typeof target !== "object") continue;
    if (!("ResTarget" in target)) continue;
    const rt = target.ResTarget;
    visitForCorrelation(rt.val, outerIds, (alias) => {
      hit = { correlatedAlias: alias };
    });
  }
  if (!hit) return null;
  const h = hit;
  const result = {
    code: "SQL-010",
    title: "Correlated subquery in SELECT projection",
    severity: "warn",
    confidence: 75,
    detail: `A scalar subquery in the SELECT projection references the outer table alias \`${h.correlatedAlias}\`. Correlated subqueries in projection often produce N+1 query plans (the subquery executes once per outer row), which scales poorly compared to an explicit JOIN + aggregation rewrite.`,
    fix: "Rewrite as a JOIN with GROUP BY (or use a LATERAL join) so the database can plan the operation as a single scan rather than per-row subquery execution. The result of the rewrite is usually faster and equally readable.",
    threatCategories: ["integrity"]
  };
  return result;
};
function visitForCorrelation(expr, outerIds, onHit) {
  let stopped = false;
  astWalk(expr, (node) => {
    if (stopped) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("SubLink" in obj)) return void 0;
    const sl = obj["SubLink"];
    if (sl.subLinkType !== "EXPR_SUBLINK") return void 0;
    const subselect = sl.subselect;
    if (!subselect || typeof subselect !== "object") return void 0;
    const inner = subselect.SelectStmt;
    if (!inner) return void 0;
    const whereClause = inner["whereClause"];
    if (!whereClause) return void 0;
    const correlated = findOuterReference(whereClause, outerIds);
    if (correlated !== null) {
      stopped = true;
      onHit(correlated);
      return "stop";
    }
    return "skip";
  });
}
function findOuterReference(whereClause, outerIds) {
  let found = null;
  astWalk(whereClause, (node) => {
    if (found !== null) return "stop";
    if (!node || typeof node !== "object") return void 0;
    if (!("ColumnRef" in node)) return void 0;
    const cr = node.ColumnRef;
    const fields = cr.fields;
    if (!Array.isArray(fields) || fields.length < 2) return void 0;
    const qualifier = readStringField(fields[0]);
    if (qualifier !== null && outerIds.has(qualifier)) {
      found = qualifier;
      return "stop";
    }
    return void 0;
  });
  return found;
}
function readStringField(field) {
  if (!field || typeof field !== "object") return null;
  const f = field;
  return typeof f.String?.sval === "string" ? f.String.sval : null;
}

// src/rules/sql-011-aggregate-no-groupby.ts
var POSTGRES_AGGREGATES = /* @__PURE__ */ new Set([
  "count",
  "sum",
  "avg",
  "min",
  "max",
  "string_agg",
  "array_agg",
  "json_agg",
  "jsonb_agg",
  "json_object_agg",
  "jsonb_object_agg",
  "bool_and",
  "bool_or",
  "every",
  "bit_and",
  "bit_or",
  "xmlagg",
  "corr",
  "covar_pop",
  "covar_samp",
  "stddev",
  "stddev_pop",
  "stddev_samp",
  "variance",
  "var_pop",
  "var_samp",
  "mode",
  "percentile_cont",
  "percentile_disc"
]);
var SQL_011 = (ast) => {
  let selectStmt = null;
  astWalk(ast, (node) => {
    if (selectStmt) return "stop";
    if (node && typeof node === "object" && "SelectStmt" in node) {
      selectStmt = node.SelectStmt;
      return "stop";
    }
    return void 0;
  });
  if (!selectStmt) return null;
  const stmt = selectStmt;
  const groupClause = stmt["groupClause"];
  if (Array.isArray(groupClause) && groupClause.length > 0) return null;
  const targetList = stmt["targetList"];
  if (!Array.isArray(targetList) || targetList.length === 0) return null;
  let hasAggregate = false;
  let hasNakedColumn = false;
  let aggregateName = null;
  let nakedColumnName = null;
  const visit = (node, insideAggOrWindow) => {
    if (!node || typeof node !== "object") return;
    const obj = node;
    if ("SubLink" in obj) return;
    if ("ColumnRef" in obj) {
      if (!insideAggOrWindow) {
        hasNakedColumn = true;
        if (nakedColumnName === null) {
          nakedColumnName = readColumnName2(obj["ColumnRef"]);
        }
      }
      return;
    }
    if ("FuncCall" in obj) {
      const fc = obj["FuncCall"];
      const isWindow = fc.over !== void 0 && fc.over !== null;
      const fname = readFuncName(fc.funcname);
      const isAgg = !isWindow && fname !== null && POSTGRES_AGGREGATES.has(fname);
      if (isAgg) {
        hasAggregate = true;
        if (aggregateName === null && fname !== null) aggregateName = fname;
      }
      const newInside = insideAggOrWindow || isAgg || isWindow;
      if (Array.isArray(fc.args)) {
        for (const arg of fc.args) visit(arg, newInside);
      }
      if (fc.over !== void 0) visit(fc.over, newInside);
      if (fc.agg_filter !== void 0) visit(fc.agg_filter, newInside);
      if (Array.isArray(fc.agg_order)) {
        for (const ord of fc.agg_order) visit(ord, newInside);
      }
      return;
    }
    for (const key of Object.keys(obj)) {
      const child = obj[key];
      if (Array.isArray(child)) {
        for (const item of child) visit(item, insideAggOrWindow);
      } else if (child && typeof child === "object") {
        visit(child, insideAggOrWindow);
      }
    }
  };
  for (const target of targetList) {
    if (!target || typeof target !== "object") continue;
    if (!("ResTarget" in target)) continue;
    const rt = target.ResTarget;
    visit(rt.val, false);
  }
  if (!hasAggregate || !hasNakedColumn) return null;
  const result = {
    code: "SQL-011",
    title: "Aggregate with non-aggregated column and no GROUP BY",
    severity: "warn",
    confidence: 90,
    detail: `The SELECT mixes an aggregate \`${aggregateName ?? "<aggregate>"}()\` with a non-aggregated column \`${nakedColumnName ?? "<column>"}\`, but there is no GROUP BY clause. Postgres rejects this query at runtime with "column must appear in the GROUP BY clause or be used in an aggregate function".`,
    fix: "Either add a GROUP BY clause naming the non-aggregated columns, wrap the bare column reference in an aggregate (e.g. `max(name)`), or drop the bare column from the projection if it isn't actually needed.",
    threatCategories: ["corruption"]
  };
  return result;
};
function readFuncName(funcname) {
  if (!Array.isArray(funcname) || funcname.length === 0) return null;
  const last = funcname[funcname.length - 1];
  if (!last || typeof last !== "object") return null;
  const f = last;
  return typeof f.String?.sval === "string" ? f.String.sval.toLowerCase() : null;
}
function readColumnName2(columnRef) {
  if (!columnRef || typeof columnRef !== "object") return null;
  const cr = columnRef;
  if (!Array.isArray(cr.fields) || cr.fields.length === 0) return null;
  const last = cr.fields[cr.fields.length - 1];
  if (!last || typeof last !== "object") return null;
  const f = last;
  if (f.A_Star) return "*";
  return typeof f.String?.sval === "string" ? f.String.sval : null;
}
function readQualifiedColumnName(columnRef) {
  if (!columnRef || typeof columnRef !== "object") return null;
  const cr = columnRef;
  if (!Array.isArray(cr.fields) || cr.fields.length === 0) return null;
  const parts = [];
  for (const field of cr.fields) {
    if (!field || typeof field !== "object") return null;
    const f = field;
    if (f.A_Star) return null;
    if (typeof f.String?.sval !== "string") return null;
    parts.push(f.String.sval);
  }
  return parts.length > 0 ? parts.join(".") : null;
}
var SQL_011_FIX = {
  fix(ast, sql) {
    if (SQL_011(ast) === null) return null;
    let qualifiedColumn = null;
    const findVisit = (node, insideAggOrWindow) => {
      if (qualifiedColumn !== null) return;
      if (!node || typeof node !== "object") return;
      const obj = node;
      if ("ColumnRef" in obj) {
        if (!insideAggOrWindow) {
          qualifiedColumn = readQualifiedColumnName(obj["ColumnRef"]);
        }
        return;
      }
      if ("FuncCall" in obj) {
        const fc = obj["FuncCall"];
        const isWindow = fc.over !== void 0 && fc.over !== null;
        const fname = readFuncName(fc.funcname);
        const isAgg = !isWindow && fname !== null && POSTGRES_AGGREGATES.has(fname);
        const newInside = insideAggOrWindow || isAgg || isWindow;
        if (Array.isArray(fc.args)) for (const a of fc.args) findVisit(a, newInside);
        if (fc.over !== void 0) findVisit(fc.over, newInside);
        if (fc.agg_filter !== void 0) findVisit(fc.agg_filter, newInside);
        if (Array.isArray(fc.agg_order))
          for (const o of fc.agg_order) findVisit(o, newInside);
        return;
      }
      for (const key of Object.keys(obj)) {
        const child = obj[key];
        if (Array.isArray(child)) {
          for (const item of child) findVisit(item, insideAggOrWindow);
        } else if (child && typeof child === "object") {
          findVisit(child, insideAggOrWindow);
        }
      }
    };
    let targetList = null;
    astWalk(ast, (node) => {
      if (targetList) return "stop";
      if (node && typeof node === "object" && "SelectStmt" in node) {
        const ss = node.SelectStmt;
        const tl = ss["targetList"];
        if (Array.isArray(tl)) targetList = tl;
        return "stop";
      }
      return void 0;
    });
    if (!targetList) return null;
    const list = targetList;
    for (const target of list) {
      if (!target || typeof target !== "object") continue;
      if (!("ResTarget" in target)) continue;
      const rt = target.ResTarget;
      findVisit(rt.val, false);
      if (qualifiedColumn !== null) break;
    }
    if (qualifiedColumn === null) return null;
    const masked = maskStringLiterals(sql);
    const trailingClauseRe = /\b(HAVING|ORDER\s+BY|LIMIT|OFFSET|FETCH|FOR\s+UPDATE|FOR\s+NO\s+KEY\s+UPDATE|FOR\s+SHARE|FOR\s+KEY\s+SHARE)\b/i;
    const trailing = masked.match(trailingClauseRe);
    const groupByClause = ` GROUP BY ${qualifiedColumn} `;
    if (trailing && trailing.index !== void 0) {
      const pos = trailing.index;
      const before = sql.slice(0, pos).replace(/\s+$/, "");
      const after = sql.slice(pos);
      return `${before}${groupByClause}${after}`;
    }
    const trailingSemi = /;\s*$/.exec(sql);
    if (trailingSemi) {
      const cut = trailingSemi.index;
      const before = sql.slice(0, cut).replace(/\s+$/, "");
      return `${before} GROUP BY ${qualifiedColumn}${sql.slice(cut)}`;
    }
    const trimmed = sql.replace(/\s+$/, "");
    return `${trimmed} GROUP BY ${qualifiedColumn}
`;
  }
};

// src/rules/sql-012-recursive-cte.ts
var BOUNDING_OPS = /* @__PURE__ */ new Set(["<", "<=", ">", ">=", "=", "<>"]);
var SQL_012 = (ast) => {
  let hit = null;
  astWalk(ast, (node, key) => {
    if (hit) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (key !== "withClause") return void 0;
    if (obj["recursive"] !== true) return void 0;
    if (!Array.isArray(obj["ctes"])) return void 0;
    const ctes = obj["ctes"];
    for (const cteWrap of ctes) {
      if (!cteWrap || typeof cteWrap !== "object") continue;
      const cte = cteWrap.CommonTableExpr;
      if (!cte) continue;
      const cteName = cte.ctename;
      if (typeof cteName !== "string") continue;
      const body = cte.ctequery;
      if (!body || typeof body !== "object") continue;
      const sel = body.SelectStmt;
      if (!sel) continue;
      if (sel["op"] !== "SETOP_UNION") continue;
      const rarg = sel["rarg"];
      if (!rarg || typeof rarg !== "object") continue;
      const recursiveArm = rarg;
      if (!hasSelfReference(recursiveArm["fromClause"], cteName)) continue;
      const where = recursiveArm["whereClause"];
      if (where && hasBoundingPredicate(where)) continue;
      hit = { cteName };
      return "stop";
    }
    return void 0;
  });
  if (!hit) return null;
  const h = hit;
  const result = {
    code: "SQL-012",
    title: "Recursive CTE without obvious termination",
    severity: "block",
    confidence: 85,
    detail: `The recursive CTE \`${h.cteName}\` has a self-referencing recursive arm with no comparison against a numeric constant to bound the recursion. Without an explicit termination condition, Postgres iterates the recursion until it exhausts memory \u2014 a denial-of-service shape on production data.`,
    fix: `Add a depth-bounding predicate to the recursive arm's WHERE clause (e.g. \`WHERE n < 100\` or \`WHERE depth < 10\`). If the recursion is genuinely bounded by data shape rather than a constant (e.g. tree walks where the data structure has finite depth), add an explicit depth-counter column anyway \u2014 production data shape changes, and a depth limit is cheap insurance.`,
    threatCategories: ["denial-of-service"]
  };
  return result;
};
function hasSelfReference(fromClause, cteName) {
  if (!fromClause) return false;
  let found = false;
  astWalk(fromClause, (node) => {
    if (found) return "stop";
    if (!node || typeof node !== "object") return void 0;
    if (!("RangeVar" in node)) return void 0;
    const rv = node.RangeVar;
    if (rv.relname === cteName) {
      found = true;
      return "stop";
    }
    return void 0;
  });
  return found;
}
function hasBoundingPredicate(whereClause) {
  let found = false;
  astWalk(whereClause, (node) => {
    if (found) return "stop";
    if (!node || typeof node !== "object") return void 0;
    if (!("A_Expr" in node)) return void 0;
    const a = node.A_Expr;
    if (a["kind"] !== "AEXPR_OP") return void 0;
    const op = readOperatorName2(a["name"]);
    if (op === null || !BOUNDING_OPS.has(op)) return void 0;
    if (isNumericLiteral(a["lexpr"]) || isNumericLiteral(a["rexpr"])) {
      found = true;
      return "stop";
    }
    return void 0;
  });
  return found;
}
function readOperatorName2(name) {
  if (!Array.isArray(name) || name.length === 0) return null;
  const first = name[0];
  if (!first || typeof first !== "object") return null;
  const f = first;
  return typeof f.String?.sval === "string" ? f.String.sval : null;
}
function isNumericLiteral(node) {
  if (!node || typeof node !== "object") return false;
  if (!("A_Const" in node)) return false;
  const c = node.A_Const;
  if (c["isnull"] === true) return false;
  return "ival" in c || "fval" in c;
}

// src/rules/sql-013-drop-truncate.ts
var SQL_013 = (ast) => {
  const targets = [];
  astWalk(ast, (node) => {
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if ("DropStmt" in obj) {
      const stmt = obj["DropStmt"];
      const removeType = stmt["removeType"];
      const behavior = stmt["behavior"];
      const cascade = behavior === "DROP_CASCADE";
      const objects = stmt["objects"];
      if (removeType === "OBJECT_TABLE") {
        targets.push({
          kind: "TABLE",
          names: extractObjectNames(objects),
          cascade
        });
        return void 0;
      }
      if (removeType === "OBJECT_SCHEMA") {
        targets.push({
          kind: "SCHEMA",
          names: extractObjectNames(objects),
          cascade
        });
        return void 0;
      }
      if (removeType === "OBJECT_INDEX") {
        targets.push({
          kind: "INDEX",
          names: extractObjectNames(objects),
          cascade
        });
        return void 0;
      }
      return void 0;
    }
    if ("DropdbStmt" in obj) {
      const stmt = obj["DropdbStmt"];
      const dbname = typeof stmt["dbname"] === "string" ? stmt["dbname"] : "<unknown>";
      targets.push({
        kind: "DATABASE",
        names: [dbname],
        cascade: false
      });
      return void 0;
    }
    if ("TruncateStmt" in obj) {
      const stmt = obj["TruncateStmt"];
      targets.push({
        kind: "TRUNCATE",
        names: extractTruncateRelations(stmt["relations"]),
        cascade: stmt["behavior"] === "DROP_CASCADE"
      });
      return void 0;
    }
    return void 0;
  });
  if (targets.length === 0) return null;
  const firstDestructive = targets.find((x) => x.kind !== "INDEX");
  const t = firstDestructive ?? targets[0];
  const totalCount = targets.length;
  const otherTargets = targets.filter((x) => x !== t);
  if (t.kind === "INDEX") {
    const countSuffix2 = totalCount > 1 ? ` \xD7 ${totalCount}` : "";
    const additional2 = formatAdditional(otherTargets);
    const result2 = {
      code: "SQL-013",
      title: `DROP INDEX${countSuffix2}`,
      severity: "warn",
      confidence: 85,
      detail: `DROP INDEX targets [${formatNames(t.names)}]. Index drops are sometimes legitimate maintenance, but more often they are agent-generated mistakes that silently degrade query performance \u2014 Postgres does not warn when an indexed predicate falls back to a sequential scan.` + additional2,
      fix: `Verify the drop is intended. If you're replacing an index, consider CREATE INDEX CONCURRENTLY for the replacement before dropping the old one. If you're freeing write capacity, log the drop in a migration record so you can restore it later.`,
      threatCategories: ["destruction"]
    };
    return result2;
  }
  const verb = describeKind(t.kind);
  const cascadeNote = t.cascade ? " Combined with CASCADE, this also drops every dependent object (foreign keys, views, functions) without further prompting." : "";
  const countSuffix = totalCount > 1 ? ` \xD7 ${totalCount}` : "";
  const additional = formatAdditional(otherTargets);
  const result = {
    code: "SQL-013",
    title: `${titleFor(t.kind)}${countSuffix}`,
    severity: "block",
    confidence: 99,
    detail: `${verb} on [${formatNames(t.names)}] is irreversible.${cascadeNote} In an AI-agent context this is essentially never the intended operation \u2014 the agent's plan almost certainly meant to remove specific rows, not the entire object.` + additional,
    fix: `Verify the destruction is intended and use a migration tool with a confirmation step. Consider RENAME TABLE ... TO __soft_deleted_<n> as a reversible alternative \u2014 it lets you re-validate the agent's plan against real data before the original is unrecoverable.`,
    threatCategories: ["destruction"]
  };
  return result;
};
function describeKind(kind) {
  switch (kind) {
    case "TABLE":
      return "DROP TABLE";
    case "DATABASE":
      return "DROP DATABASE";
    case "SCHEMA":
      return "DROP SCHEMA";
    case "TRUNCATE":
      return "TRUNCATE";
    case "INDEX":
      return "DROP INDEX";
  }
}
function titleFor(kind) {
  switch (kind) {
    case "TABLE":
      return "DROP TABLE \u2014 irreversible table destruction";
    case "DATABASE":
      return "DROP DATABASE \u2014 irreversible database destruction";
    case "SCHEMA":
      return "DROP SCHEMA \u2014 irreversible schema destruction";
    case "TRUNCATE":
      return "TRUNCATE \u2014 non-recoverable bulk row deletion";
    case "INDEX":
      return "DROP INDEX";
  }
}
function formatNames(names) {
  return names.length > 0 ? names.join(", ") : "<unknown>";
}
function formatAdditional(others) {
  if (others.length === 0) return "";
  const lines = others.map(
    (o) => `  - ${describeKind(o.kind)} [${formatNames(o.names)}]`
  );
  return `

Additional destructive statements in this script (${others.length}):
${lines.join("\n")}`;
}
function extractObjectNames(objects) {
  if (!Array.isArray(objects)) return [];
  const out = [];
  for (const entry of objects) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry;
    if ("List" in e) {
      const list = e["List"];
      const items = list?.["items"];
      if (Array.isArray(items)) {
        const parts = [];
        for (const item of items) {
          const s2 = readStringSval(item);
          if (s2 !== null) parts.push(s2);
        }
        if (parts.length > 0) out.push(parts.join("."));
      }
      continue;
    }
    const s = readStringSval(entry);
    if (s !== null) out.push(s);
  }
  return out;
}
function extractTruncateRelations(relations) {
  if (!Array.isArray(relations)) return [];
  const out = [];
  for (const entry of relations) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry;
    if (!("RangeVar" in e)) continue;
    const rv = e["RangeVar"];
    const relname = typeof rv["relname"] === "string" ? rv["relname"] : "";
    const schemaname = typeof rv["schemaname"] === "string" ? `${rv["schemaname"]}.` : "";
    if (relname) out.push(`${schemaname}${relname}`);
  }
  return out;
}
function readStringSval(node) {
  if (!node || typeof node !== "object") return null;
  const n = node;
  if (!("String" in n)) return null;
  const s = n["String"];
  return typeof s["sval"] === "string" ? s["sval"] : null;
}

// src/rules/sql-014-missing-returning.ts
var SQL_014 = (ast) => {
  let target = null;
  astWalk(ast, (node) => {
    if (target) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if ("InsertStmt" in obj) {
      const stmt = obj["InsertStmt"];
      if (lacksReturning(stmt)) {
        target = {
          stmtType: "INSERT",
          tableName: extractRelationName2(stmt["relation"])
        };
        return "stop";
      }
      return void 0;
    }
    if ("UpdateStmt" in obj) {
      const stmt = obj["UpdateStmt"];
      if (lacksReturning(stmt)) {
        target = {
          stmtType: "UPDATE",
          tableName: extractRelationName2(stmt["relation"])
        };
        return "stop";
      }
      return void 0;
    }
    if ("DeleteStmt" in obj) {
      const stmt = obj["DeleteStmt"];
      if (lacksReturning(stmt)) {
        target = {
          stmtType: "DELETE",
          tableName: extractRelationName2(stmt["relation"])
        };
        return "stop";
      }
      return void 0;
    }
    return void 0;
  });
  if (!target) return null;
  const t = target;
  const verb = t.stmtType.toLowerCase();
  const result = {
    code: "SQL-014",
    title: `${t.stmtType} without RETURNING`,
    severity: "info",
    confidence: 50,
    detail: `${t.stmtType} on \`${t.tableName}\` does not include a RETURNING clause. If your agent's plan needs the affected row back \u2014 to confirm the new id, surface state to the caller, or chain a follow-up query \u2014 without RETURNING the agent has to re-query or proceed blind. This rule is opt-in because not every ${verb} needs RETURNING; enable it when the agent's prompts routinely ask "and tell me the affected row."`,
    fix: `Add a RETURNING clause that names the columns you'll read back, e.g. \`${t.stmtType} ... RETURNING id, updated_at\`. Postgres executes RETURNING in the same transaction so there's no additional round-trip cost.`,
    threatCategories: ["integrity"]
  };
  return result;
};
function lacksReturning(stmt) {
  const rl = stmt["returningList"];
  if (rl === void 0 || rl === null) return true;
  if (Array.isArray(rl) && rl.length === 0) return true;
  return false;
}
function extractRelationName2(relation) {
  if (!relation || typeof relation !== "object") return "<unknown>";
  const r = relation;
  const schema = typeof r.schemaname === "string" ? `${r.schemaname}.` : "";
  const name = typeof r.relname === "string" ? r.relname : "<unknown>";
  return `${schema}${name}`;
}

// src/rules/sql-015-select-star.ts
var SQL_015 = (ast) => {
  const existsSelects = collectExistsSubselects(ast);
  let target = null;
  astWalk(ast, (node, key) => {
    if (target) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if ("SelectStmt" in obj) {
      const sel = obj["SelectStmt"];
      if (sel && typeof sel === "object" && existsSelects.has(sel)) {
        return "skip";
      }
    }
    if (!("ResTarget" in obj)) return void 0;
    if (key !== "targetList") return void 0;
    const resTarget = obj["ResTarget"];
    const val = resTarget["val"];
    const star = readStarColumnRef(val);
    if (star) {
      target = star;
      return "stop";
    }
    return void 0;
  });
  if (!target) return null;
  const t = target;
  const projection = t.qualified ? `${t.qualifier}.*` : "*";
  const result = {
    code: "SQL-015",
    title: t.qualified ? "SELECT t.* \u2014 qualified over-fetch" : "SELECT * \u2014 projection over-fetch",
    severity: "info",
    confidence: 60,
    detail: `Projection \`${projection}\` returns every column in the source relation. For an AI agent this has three costs: token spend on columns the agent never reads, exposure of any sensitive columns the table happens to carry, and silent breakage when the schema gains new columns the consumer's parser doesn't expect.`,
    fix: `Replace the star with the explicit column list you actually need, e.g. \`SELECT id, email, created_at FROM users\`. If you genuinely need every column (one-off admin / debug paths), keep the star but constrain the result with a tight LIMIT and consider running it under operator review.`,
    threatCategories: ["exfiltration", "integrity"]
  };
  return result;
};
function readStarColumnRef(val) {
  if (!val || typeof val !== "object") return null;
  const v = val;
  if (!("ColumnRef" in v)) return null;
  const cr = v["ColumnRef"];
  const fields = cr["fields"];
  if (!Array.isArray(fields) || fields.length === 0) return null;
  const last = fields[fields.length - 1];
  if (!last || typeof last !== "object") return null;
  const lastObj = last;
  if (!("A_Star" in lastObj)) return null;
  if (fields.length === 1) {
    return { qualified: false, qualifier: null };
  }
  const first = fields[0];
  const qualifier = readStringSval2(first);
  return { qualified: true, qualifier };
}
function collectExistsSubselects(ast) {
  const out = /* @__PURE__ */ new WeakSet();
  astWalk(ast, (node) => {
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("SubLink" in obj)) return void 0;
    const sl = obj["SubLink"];
    if (!sl || typeof sl !== "object") return void 0;
    const slObj = sl;
    const t = slObj["subLinkType"];
    if (t !== "EXISTS_SUBLINK" && t !== "NOT_EXISTS_SUBLINK") return void 0;
    const sub = slObj["subselect"];
    if (!sub || typeof sub !== "object") return void 0;
    const subObj = sub;
    const select = subObj["SelectStmt"];
    if (select && typeof select === "object") {
      out.add(select);
    }
    return void 0;
  });
  return out;
}
function readStringSval2(node) {
  if (!node || typeof node !== "object") return null;
  const n = node;
  if (!("String" in n)) return null;
  const s = n["String"];
  return typeof s["sval"] === "string" ? s["sval"] : null;
}

// src/rules/stmt-dispatch.ts
function hasTopLevelStmt(ast, kinds) {
  if (!ast || typeof ast !== "object") return false;
  const stmts = ast.stmts;
  if (!Array.isArray(stmts)) return false;
  for (const wrapper of stmts) {
    if (!wrapper || typeof wrapper !== "object") continue;
    const inner = wrapper.stmt;
    if (!inner || typeof inner !== "object") continue;
    for (const k of Object.keys(inner)) {
      if (kinds.has(k)) return true;
    }
  }
  return false;
}

// src/rules/sql-016-copy-program.ts
var SQL_016 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["CopyStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("CopyStmt" in obj)) return void 0;
    const stmt = obj["CopyStmt"];
    if (stmt["is_program"] !== true) return void 0;
    fired = {
      command: typeof stmt["filename"] === "string" ? stmt["filename"] : "<unknown>",
      isFrom: stmt["is_from"] === true
    };
    return "stop";
  });
  if (!fired) return null;
  const f = fired;
  const result = {
    code: "SQL-016",
    title: f.isFrom ? "COPY ... FROM PROGRAM \u2014 server-side RCE" : "COPY ... TO PROGRAM \u2014 server-side RCE",
    severity: "block",
    confidence: 99,
    detail: `COPY ... ${f.isFrom ? "FROM" : "TO"} PROGRAM '${f.command}' runs an arbitrary shell command on the database host under the postgres OS-user's privileges (Postgres invokes /bin/sh -c on the program string). It is documented superuser-only because it is RCE \u2014 there is essentially no agent workflow that should issue this.`,
    fix: `Replace COPY ... PROGRAM with one of: COPY ... TO STDOUT / FROM STDIN (driver-side streaming, no server-side shell), COPY ... TO/FROM '<file path>' (no shell expansion), or read/write the data via the application instead of the database.`,
    threatCategories: ["exfiltration", "injection"]
  };
  return result;
};

// src/rules/sql-017-untrusted-extension.ts
var UNTRUSTED_LANGS = /* @__PURE__ */ new Set([
  "plpython3u",
  "plpython2u",
  "plpythonu",
  "plperlu",
  "pltclu",
  "plr",
  "plsh"
]);
var SQL_017 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["CreateExtensionStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("CreateExtensionStmt" in obj)) return void 0;
    const stmt = obj["CreateExtensionStmt"];
    const name = typeof stmt["extname"] === "string" ? stmt["extname"].toLowerCase() : "";
    if (UNTRUSTED_LANGS.has(name)) {
      fired = name;
      return "stop";
    }
    return void 0;
  });
  if (!fired) return null;
  const lang = fired;
  const result = {
    code: "SQL-017",
    title: `CREATE EXTENSION ${lang} \u2014 untrusted procedural language`,
    severity: "block",
    confidence: 95,
    detail: `${lang} is a documented "untrusted" Postgres procedural language \u2014 functions written in it run as the postgres OS-user with no language-level sandbox (full filesystem and network access). Installing it is a permanent RCE primitive: any subsequent CREATE FUNCTION ... LANGUAGE ${lang} can execute arbitrary shell commands.`,
    fix: `Use a TRUSTED procedural language (\`plpgsql\`, \`plperl\`, \`pltcl\`, \`sql\`) if a procedural language is needed. If the agent's plan genuinely needs server-side scripting in a U-language, route the install through an operator-confirmed migration step rather than agent-issued SQL.`,
    threatCategories: ["injection"]
  };
  return result;
};

// src/rules/sql-018-drop-column.ts
var SQL_018 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["AlterTableStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("AlterTableStmt" in obj)) return void 0;
    const stmt = obj["AlterTableStmt"];
    const cmds = stmt["cmds"];
    if (!Array.isArray(cmds)) return void 0;
    const drops = [];
    let cascade = false;
    for (const c of cmds) {
      if (!c || typeof c !== "object") continue;
      const co = c;
      if (!("AlterTableCmd" in co)) continue;
      const cmd = co["AlterTableCmd"];
      if (cmd["subtype"] !== "AT_DropColumn") continue;
      const cname = typeof cmd["name"] === "string" ? cmd["name"] : "<unknown>";
      drops.push(cname);
      if (cmd["behavior"] === "DROP_CASCADE") cascade = true;
    }
    if (drops.length === 0) return void 0;
    const rel = stmt["relation"];
    fired = {
      relname: typeof rel?.["relname"] === "string" ? rel["relname"] : "<unknown>",
      columns: drops,
      cascade
    };
    return "stop";
  });
  if (!fired) return null;
  const f = fired;
  const cascadeNote = f.cascade ? " Combined with CASCADE, this also drops any view, index, or constraint that references the column." : "";
  const result = {
    code: "SQL-018",
    title: f.columns.length === 1 ? `ALTER TABLE DROP COLUMN \u2014 irreversible schema change` : `ALTER TABLE DROP COLUMN \xD7 ${f.columns.length} \u2014 irreversible schema change`,
    severity: "warn",
    confidence: 90,
    detail: `ALTER TABLE ${f.relname} DROP COLUMN [${f.columns.join(", ")}] is irreversible. The column data cannot be recovered without a backup, and any downstream reader (other services, materialized views, cached query plans) breaks the next time it touches the column.${cascadeNote}`,
    fix: `If the column is genuinely unused, route the drop through a migration tool with a reversible step. As an interim, RENAME the column (e.g. \`ALTER TABLE ${f.relname} RENAME COLUMN ${f.columns[0] ?? "<col>"} TO __deprecated_${f.columns[0] ?? "<col>"}\`) \u2014 reads of the new name fail loudly while the data remains available for rollback.`,
    threatCategories: ["destruction"]
  };
  return result;
};

// src/rules/sql-019-create-trigger.ts
var SQL_019 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["CreateTrigStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("CreateTrigStmt" in obj)) return void 0;
    const stmt = obj["CreateTrigStmt"];
    const rel = stmt["relation"];
    fired = {
      trigname: typeof stmt["trigname"] === "string" ? stmt["trigname"] : "<unknown>",
      relname: typeof rel?.["relname"] === "string" ? rel["relname"] : "<unknown>",
      replace: stmt["replace"] === true
    };
    return "stop";
  });
  if (!fired) return null;
  const f = fired;
  const verb = f.replace ? "CREATE OR REPLACE TRIGGER" : "CREATE TRIGGER";
  const result = {
    code: "SQL-019",
    title: `${verb} \u2014 hidden side effect on every matching row event`,
    severity: "info",
    confidence: 75,
    detail: `${verb} \`${f.trigname}\` on \`${f.relname}\` installs procedural code that runs on every matching row event from now on. From an agent's perspective this is a permanent, invisible control-flow change \u2014 the trigger keeps firing long after the migration that installed it has been forgotten.`,
    fix: `Verify the trigger is intended. If you are migrating logic into a trigger, confirm the trigger function's body is safe (it can call PERFORM dblink(...), read pg_read_server_files(...), or escalate via SECURITY DEFINER). Document the trigger in the migration log so future operators know it exists.`,
    threatCategories: ["integrity"]
  };
  return result;
};

// src/rules/sql-020-create-or-replace-function.ts
var SQL_020 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["CreateFunctionStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("CreateFunctionStmt" in obj)) return void 0;
    const stmt = obj["CreateFunctionStmt"];
    if (stmt["replace"] !== true) return void 0;
    const fn = stmt["funcname"];
    if (Array.isArray(fn)) {
      const parts = [];
      for (const it of fn) {
        if (it && typeof it === "object") {
          const s = it["String"];
          if (s && typeof s === "object") {
            const sval = s["sval"];
            if (typeof sval === "string") parts.push(sval);
          }
        }
      }
      fired = parts.length > 0 ? parts.join(".") : "<unknown>";
    } else {
      fired = "<unknown>";
    }
    return "stop";
  });
  if (!fired) return null;
  const fname = fired;
  const result = {
    code: "SQL-020",
    title: `CREATE OR REPLACE FUNCTION \u2014 silent overwrite`,
    severity: "info",
    confidence: 70,
    detail: `\`CREATE OR REPLACE FUNCTION ${fname}\` silently overwrites any existing function with the same signature. If a SECURITY DEFINER function with that name already exists, the OR REPLACE swaps in the new body without further confirmation. Static analysis cannot tell whether a previous function existed \u2014 the catch fires on the agent's stated intent to overwrite.`,
    fix: `If you do not intend to overwrite an existing function, drop the OR REPLACE keyword \u2014 Postgres will refuse the statement if a function with that signature already exists, surfacing the collision instead of hiding it.`,
    threatCategories: ["integrity"]
  };
  return result;
};

// src/rules/sql-021-grant-public.ts
var SQL_021 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["GrantStmt"]))) return null;
  let fired = false;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("GrantStmt" in obj)) return void 0;
    const stmt = obj["GrantStmt"];
    if (stmt["is_grant"] !== true) return void 0;
    const grantees = stmt["grantees"];
    if (!Array.isArray(grantees)) return void 0;
    for (const g of grantees) {
      if (!g || typeof g !== "object") continue;
      const ro = g["RoleSpec"];
      if (!ro || typeof ro !== "object") continue;
      if (ro["roletype"] === "ROLESPEC_PUBLIC") {
        fired = true;
        return "stop";
      }
    }
    return void 0;
  });
  if (!fired) return null;
  const result = {
    code: "SQL-021",
    title: `GRANT ... TO PUBLIC \u2014 over-broad permission`,
    severity: "warn",
    confidence: 90,
    detail: `GRANT ... TO PUBLIC widens the privilege to every current and future role in the database. Once granted, every new role automatically receives the privilege; revoking it later requires also revoking from every role that has been created since. This is almost always over-broad relative to the agent's stated intent.`,
    fix: `Replace PUBLIC with the specific role(s) that need the privilege (e.g. \`GRANT SELECT ON users TO app_reader\`). For legitimately public helpers (e.g. a UDF anyone may call), document the PUBLIC grant in the migration so future operators know it is intentional.`,
    threatCategories: ["exfiltration"]
  };
  return result;
};

// src/rules/sql-022-superuser-role.ts
var SQL_022 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["CreateRoleStmt", "AlterRoleStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    let stmt = null;
    let verb = null;
    if ("CreateRoleStmt" in obj) {
      stmt = obj["CreateRoleStmt"];
      verb = "CREATE ROLE";
    } else if ("AlterRoleStmt" in obj) {
      stmt = obj["AlterRoleStmt"];
      verb = "ALTER ROLE";
    } else {
      return void 0;
    }
    const opts = stmt["options"];
    if (!Array.isArray(opts)) return void 0;
    let hasSuperuser = false;
    for (const o of opts) {
      if (!o || typeof o !== "object") continue;
      const de = o["DefElem"];
      if (!de || typeof de !== "object") continue;
      const d = de;
      if (d["defname"] !== "superuser") continue;
      const arg = d["arg"];
      if (arg && typeof arg === "object") {
        const b = arg["Boolean"];
        if (b && typeof b === "object" && b["boolval"] === true) {
          hasSuperuser = true;
          break;
        }
      }
    }
    if (!hasSuperuser) return void 0;
    let rolename = "<unknown>";
    if (verb === "CREATE ROLE") {
      if (typeof stmt["role"] === "string") rolename = stmt["role"];
    } else {
      const rs = stmt["role"];
      if (rs && typeof rs["rolename"] === "string") rolename = rs["rolename"];
    }
    fired = { verb, rolename };
    return "stop";
  });
  if (!fired) return null;
  const f = fired;
  const result = {
    code: "SQL-022",
    title: `${f.verb} ... SUPERUSER \u2014 privilege escalation`,
    severity: "block",
    confidence: 95,
    detail: `${f.verb} \`${f.rolename}\` ... SUPERUSER bypasses every permission check in Postgres, including row-level security, GRANT/REVOKE, and the LANGUAGE-trust whitelist for procedural functions. Once granted it provides a permanent privilege-escalation primitive \u2014 any subsequent agent-issued SQL run as this role can drop tables, install untrusted extensions, or read arbitrary server files.`,
    fix: `Drop the SUPERUSER attribute. If the role legitimately needs elevated read or replication access, GRANT one of the named predefined roles instead (e.g. \`pg_read_all_data\`, \`pg_monitor\`, \`pg_write_all_data\`). True superuser bootstrap belongs to a one-off operator workflow, not agent-issued SQL.`,
    threatCategories: ["injection"]
  };
  return result;
};

// src/rules/func-names.ts
var cache = /* @__PURE__ */ new WeakMap();
function buildSet(ast) {
  const out = /* @__PURE__ */ new Set();
  astWalk(ast, (node) => {
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("FuncCall" in obj)) return void 0;
    const fc = obj["FuncCall"];
    const fn = fc["funcname"];
    if (!Array.isArray(fn) || fn.length === 0) return void 0;
    const last = fn[fn.length - 1];
    if (!last || typeof last !== "object") return void 0;
    const s = last["String"];
    if (!s || typeof s !== "object") return void 0;
    const sval = s["sval"];
    if (typeof sval === "string") {
      out.add(sval.toLowerCase());
    }
    return void 0;
  });
  return out;
}
function extractFuncNames(ast) {
  if (!ast || typeof ast !== "object") return /* @__PURE__ */ new Set();
  const cached = cache.get(ast);
  if (cached) return cached;
  const set = buildSet(ast);
  cache.set(ast, set);
  return set;
}
function firstFuncNameMatch(ast, targets) {
  const present = extractFuncNames(ast);
  for (const t of targets) {
    if (present.has(t)) return t;
  }
  return null;
}

// src/rules/sql-023-pg-terminate-backend.ts
var TARGET_FUNCS = /* @__PURE__ */ new Set(["pg_terminate_backend", "pg_cancel_backend"]);
var SQL_023 = (ast) => {
  const fired = firstFuncNameMatch(ast, TARGET_FUNCS);
  if (!fired) return null;
  const verb = fired === "pg_terminate_backend" ? "pg_terminate_backend" : "pg_cancel_backend";
  const action = fired === "pg_terminate_backend" ? "forcibly disconnects" : "cancels the active query of";
  const result = {
    code: "SQL-023",
    title: `${verb} \u2014 session-control DoS primitive`,
    severity: "warn",
    confidence: 85,
    detail: `${verb}() ${action} another Postgres session. Used in bulk against \`pg_stat_activity\` it is a one-shot DoS primitive: every other session is disconnected or has its query killed. Even single-pid uses are unusual outside of operator workflows.`,
    fix: `If the agent genuinely needs to kill a runaway query, scope the call to a specific pid (\`SELECT ${verb}(${"12345"}::int)\`) and add \`AND pid <> pg_backend_pid()\` to any \`FROM pg_stat_activity\` filter so the agent does not kill its own session in transit. For most workflows the right answer is to route session-control through an operator dashboard rather than agent-issued SQL.`,
    threatCategories: ["denial-of-service"]
  };
  return result;
};

// src/rules/sql-024-vacuum-full.ts
var SQL_024 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["VacuumStmt"]))) return null;
  let fired = false;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("VacuumStmt" in obj)) return void 0;
    const stmt = obj["VacuumStmt"];
    if (stmt["is_vacuumcmd"] !== true) return void 0;
    const opts = stmt["options"];
    if (!Array.isArray(opts)) return void 0;
    for (const o of opts) {
      if (!o || typeof o !== "object") continue;
      const de = o["DefElem"];
      if (!de || typeof de !== "object") continue;
      if (de["defname"] === "full") {
        fired = true;
        return "stop";
      }
    }
    return void 0;
  });
  if (!fired) return null;
  const result = {
    code: "SQL-024",
    title: `VACUUM FULL \u2014 ACCESS EXCLUSIVE outage`,
    severity: "warn",
    confidence: 80,
    detail: `VACUUM FULL takes an ACCESS EXCLUSIVE lock on the target relation and rewrites the entire table file. On a busy production table that means every reader and writer blocks until the rewrite finishes \u2014 effectively an outage on the table. The classic alternative (\`pg_repack\` or \`VACUUM\` without FULL) avoids the exclusive lock.`,
    fix: `If you need to reclaim disk space online, use \`pg_repack\` (extension) or schedule the VACUUM FULL inside a deliberate maintenance window. For routine bloat control, plain \`VACUUM\` (no FULL) takes only a SHARE UPDATE EXCLUSIVE lock and does not block reads or writes.`,
    threatCategories: ["denial-of-service"]
  };
  return result;
};

// src/rules/sql-025-refresh-matview.ts
var SQL_025 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["RefreshMatViewStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("RefreshMatViewStmt" in obj)) return void 0;
    const stmt = obj["RefreshMatViewStmt"];
    if (stmt["concurrent"] === true) return void 0;
    const rel = stmt["relation"];
    fired = typeof rel?.["relname"] === "string" ? rel["relname"] : "<unknown>";
    return "stop";
  });
  if (!fired) return null;
  const mv = fired;
  const result = {
    code: "SQL-025",
    title: `REFRESH MATERIALIZED VIEW \u2014 blocking refresh`,
    severity: "warn",
    confidence: 75,
    detail: `REFRESH MATERIALIZED VIEW \`${mv}\` (without CONCURRENTLY) takes an ACCESS EXCLUSIVE lock on the view and blocks every reader until the refresh finishes. On a large MV that is a substantial production stall \u2014 the CONCURRENTLY variant exists precisely to avoid it.`,
    fix: `Use \`REFRESH MATERIALIZED VIEW CONCURRENTLY ${mv}\` instead. CONCURRENTLY requires the view to have at least one UNIQUE index and to have been populated by a prior non-concurrent refresh; once those one-time prerequisites are met every subsequent refresh can be online.`,
    threatCategories: ["denial-of-service"]
  };
  return result;
};

// src/rules/is-tautology.ts
function asConstSval(node) {
  if (!node || typeof node !== "object") return void 0;
  const c = node["A_Const"];
  if (!c || typeof c !== "object") return void 0;
  const sv = c["sval"];
  if (!sv || typeof sv !== "object") return void 0;
  const v = sv["sval"];
  return typeof v === "string" ? v : void 0;
}
function asConstIval(node) {
  if (!node || typeof node !== "object") return void 0;
  const c = node["A_Const"];
  if (!c || typeof c !== "object") return void 0;
  const iv = c["ival"];
  if (!iv || typeof iv !== "object") return void 0;
  const v = iv["ival"];
  if (typeof v === "number") return v;
  if (Object.keys(iv).length === 0) return 0;
  return void 0;
}
function asConstBool(node) {
  if (!node || typeof node !== "object") return void 0;
  const c = node["A_Const"];
  if (!c || typeof c !== "object") return void 0;
  const bv = c["boolval"];
  if (!bv || typeof bv !== "object") return void 0;
  const v = bv["boolval"];
  if (typeof v === "boolean") return v;
  if (Object.keys(bv).length === 0) return false;
  return void 0;
}
function columnRefPath(node) {
  if (!node || typeof node !== "object") return void 0;
  const cr = node["ColumnRef"];
  if (!cr || typeof cr !== "object") return void 0;
  const fields = cr["fields"];
  if (!Array.isArray(fields)) return void 0;
  const parts = [];
  for (const f of fields) {
    if (!f || typeof f !== "object") return void 0;
    const s = f["String"];
    if (!s || typeof s !== "object") return void 0;
    const sv = s["sval"];
    if (typeof sv !== "string") return void 0;
    parts.push(sv);
  }
  return parts.length > 0 ? parts.join(".") : void 0;
}
function isEqualityOp(opNode) {
  if (!opNode || typeof opNode !== "object") return false;
  const ax = opNode["A_Expr"];
  if (!ax || typeof ax !== "object") return false;
  if (ax["kind"] !== "AEXPR_OP") return false;
  const name = ax["name"];
  if (!Array.isArray(name) || name.length !== 1) return false;
  const n0 = name[0];
  if (!n0 || typeof n0 !== "object") return false;
  const s = n0["String"];
  if (!s || typeof s !== "object") return false;
  return s["sval"] === "=";
}
function isLiteralTautology(node) {
  if (!node || typeof node !== "object") return false;
  const directBool = asConstBool(node);
  if (directBool === true) return true;
  const be = node["BoolExpr"];
  if (be && typeof be === "object") {
    const beObj = be;
    if (beObj["boolop"] === "NOT_EXPR") {
      const args = beObj["args"];
      if (Array.isArray(args) && args.length === 1) {
        const inner = asConstBool(args[0]);
        if (inner === false) return true;
      }
    }
    return false;
  }
  if (!isEqualityOp(node)) return false;
  const ax = node["A_Expr"];
  const lex = ax["lexpr"];
  const rex = ax["rexpr"];
  const li = asConstIval(lex);
  const ri = asConstIval(rex);
  if (li !== void 0 && ri !== void 0 && li === ri) return true;
  const ls = asConstSval(lex);
  const rs = asConstSval(rex);
  if (ls !== void 0 && rs !== void 0 && ls === rs) return true;
  const lb = asConstBool(lex);
  const rb = asConstBool(rex);
  if (lb !== void 0 && rb !== void 0 && lb === rb) return true;
  const lc = columnRefPath(lex);
  const rc = columnRefPath(rex);
  if (lc !== void 0 && rc !== void 0 && lc === rc) return true;
  return false;
}

// src/rules/sql-026-merge-tautology.ts
var SQL_026 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["MergeStmt"]))) return null;
  let fired = false;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("MergeStmt" in obj)) return void 0;
    const stmt = obj["MergeStmt"];
    const joinCond = stmt["joinCondition"];
    if (isLiteralTautology(joinCond)) {
      fired = true;
      return "stop";
    }
    return void 0;
  });
  if (!fired) return null;
  const result = {
    code: "SQL-026",
    title: "MERGE with tautological ON \u2014 full-table mutation in disguise",
    severity: "block",
    confidence: 90,
    detail: "MERGE INTO ... ON <tautology> matches every source row against every target row. Combined with WHEN MATCHED THEN UPDATE or DELETE this is a blanket mutation expressed through MERGE syntax \u2014 the same threat shape that SQL-001 catches for UPDATE/DELETE, just routed through a statement type that bypasses those rules.",
    fix: "Replace the tautological ON with a real join condition that identifies the rows you intend to merge (e.g. `ON target.id = source.id`). If you genuinely intend to mutate every target row, route the change through an explicit UPDATE/DELETE so the intent is visible in the SQL.",
    threatCategories: ["destruction"]
  };
  return result;
};

// src/rules/sql-027-set-search-path.ts
var SQL_027 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["VariableSetStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired !== null) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("VariableSetStmt" in obj)) return void 0;
    const stmt = obj["VariableSetStmt"];
    if (stmt["name"] !== "search_path") return void 0;
    if (stmt["kind"] !== "VAR_SET_VALUE") return void 0;
    const args = stmt["args"];
    if (!Array.isArray(args) || args.length === 0) return void 0;
    const first = args[0];
    if (!first || typeof first !== "object") return void 0;
    const c = first["A_Const"];
    if (!c || typeof c !== "object") return void 0;
    const sv = c["sval"];
    if (!sv || typeof sv !== "object") return void 0;
    const val = sv["sval"];
    if (typeof val !== "string") return void 0;
    if (val === "pg_catalog") return void 0;
    fired = val;
    return "stop";
  });
  if (fired === null) return null;
  const firstSchema = fired;
  const result = {
    code: "SQL-027",
    title: `SET search_path puts \`${firstSchema}\` before pg_catalog \u2014 operator-shadowing risk`,
    severity: "warn",
    confidence: 85,
    detail: `SET search_path = ${firstSchema}, ... places a writable schema ahead of pg_catalog. A function or operator defined in \`${firstSchema}\` with a built-in name (e.g. an \`=(int,int)\` overload, or a \`pg_get_indexdef(oid)\` shadow) will be resolved first \u2014 the CVE-2018-1058 pattern. Any subsequent SQL run in this session can be silently rewritten by the shadowed object.`,
    fix: `Put pg_catalog first: \`SET search_path = pg_catalog, ${firstSchema}\` (or the safer fully-qualified call style: \`SET search_path = pg_catalog, pg_temp\` and qualify your own object names). The Postgres documentation recommends pg_catalog always lead.`,
    threatCategories: ["injection"]
  };
  return result;
};

// src/rules/sql-028-replication-slot.ts
var TARGET_FUNCS2 = /* @__PURE__ */ new Set([
  "pg_create_logical_replication_slot",
  "pg_create_physical_replication_slot"
]);
var SQL_028 = (ast) => {
  const fired = firstFuncNameMatch(ast, TARGET_FUNCS2);
  if (!fired) return null;
  const kind = fired.includes("logical") ? "logical" : "physical";
  const result = {
    code: "SQL-028",
    title: `${fired}() \u2014 replication slot creation`,
    severity: "warn",
    confidence: 80,
    detail: `${fired}() creates a ${kind} replication slot. A slot streams every WAL change from the database to whoever connects to it \u2014 an exfiltration channel that survives the SQL session that created it. An orphaned slot also pins WAL retention indefinitely, eventually filling the data disk (DoS).`,
    fix: `Create replication slots from an operator workflow, not from agent-issued SQL. If a slot is genuinely needed for an integration, document who owns it and ensure a reaper job will drop it (\`pg_drop_replication_slot\`) if the consumer disappears.`,
    threatCategories: ["exfiltration"]
  };
  return result;
};

// src/rules/sql-029-dblink-server.ts
var DBLINK_FUNCS = /* @__PURE__ */ new Set(["dblink_connect", "dblink", "dblink_exec", "dblink_open"]);
var SQL_029 = (ast) => {
  const hasServer = hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["CreateForeignServerStmt"]));
  let kind = null;
  let name = "";
  if (hasServer) {
    kind = "server";
  } else {
    const fn = firstFuncNameMatch(ast, DBLINK_FUNCS);
    if (fn) {
      kind = "dblink";
      name = fn;
    }
  }
  if (!kind) return null;
  const isServer = kind === "server";
  const result = {
    code: "SQL-029",
    title: isServer ? "CREATE SERVER \u2014 outbound foreign-data wrapper" : `${name}() \u2014 outbound network call`,
    severity: "warn",
    confidence: 80,
    detail: isServer ? `CREATE SERVER attaches a foreign data wrapper to a remote host; subsequent queries against foreign tables on this server make outbound network calls from the database. Created from agent-issued SQL it is an exfiltration channel \u2014 query data here, route it through the foreign server.` : `${name}() makes an outbound network call from the database server to another host. Used inside an agent prompt it is an exfiltration primitive: query data here, send it there.`,
    fix: `Outbound network capability belongs in the application layer, not in agent-issued SQL. If the workflow genuinely needs to federate across databases, route the connection through a pre-provisioned, operator-owned FOREIGN SERVER + USER MAPPING whose endpoints are not agent-controllable.`,
    threatCategories: ["exfiltration"]
  };
  return result;
};

// src/rules/sql-030-file-primitives.ts
var FILE_FUNCS = /* @__PURE__ */ new Set([
  "lo_export",
  "pg_read_server_files",
  "pg_read_server_file",
  "pg_read_binary_file",
  "pg_ls_dir"
]);
var SQL_030 = (ast) => {
  const fired = firstFuncNameMatch(ast, FILE_FUNCS);
  if (!fired) return null;
  const isWrite = fired === "lo_export";
  const result = {
    code: "SQL-030",
    title: `${fired}() \u2014 server-side filesystem ${isWrite ? "write" : "read"}`,
    severity: "warn",
    confidence: 90,
    detail: `${fired}() ${isWrite ? "writes to" : "reads from"} the database server's filesystem under the postgres OS-user's privileges. This is the canonical primitive for ${isWrite ? "planting attacker payloads on disk" : "exfiltrating arbitrary server files (e.g. /etc/passwd, configuration files, other databases' data files)"}.`,
    fix: `Server-side file I/O belongs in operator workflows, not agent-issued SQL. If the agent genuinely needs to read or write bytes, route the I/O through the application layer instead \u2014 it has the right OS-user, the right path scoping, and the right audit trail.`,
    threatCategories: ["exfiltration"]
  };
  return result;
};

// src/rules/sql-031-on-conflict-do-update.ts
var SQL_031 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["InsertStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("InsertStmt" in obj)) return void 0;
    const stmt = obj["InsertStmt"];
    const occ = stmt["onConflictClause"];
    if (!occ || occ["action"] !== "ONCONFLICT_UPDATE") return void 0;
    const sel = stmt["selectStmt"];
    if (!sel) return void 0;
    const inner = sel["SelectStmt"];
    if (!inner) return void 0;
    if ("valuesLists" in inner) return void 0;
    if (!Array.isArray(inner["fromClause"]) || inner["fromClause"].length === 0) return void 0;
    if ("limitCount" in inner && inner["limitCount"]) return void 0;
    const rel = stmt["relation"];
    fired = typeof rel?.["relname"] === "string" ? rel["relname"] : "<unknown>";
    return "stop";
  });
  if (!fired) return null;
  const target = fired;
  const result = {
    code: "SQL-031",
    title: "INSERT \u2026 SELECT \u2026 ON CONFLICT DO UPDATE \u2014 unbounded upsert",
    severity: "info",
    confidence: 75,
    detail: `INSERT INTO \`${target}\` SELECT \u2026 ON CONFLICT DO UPDATE will upsert every row produced by the inner SELECT. With no LIMIT bound and a SELECT source that overlaps the target, this is a full-table overwrite expressed through INSERT syntax \u2014 the same threat shape SQL-001 catches for blanket UPDATE/DELETE, just routed through INSERT.`,
    fix: `Add a LIMIT to the inner SELECT to bound the mutation, or replace the upsert with an explicit UPDATE whose WHERE clause makes the affected rows visible. If the unbounded upsert is a deliberate batch import, document it in the migration so future operators know it is intentional.`,
    threatCategories: ["destruction"]
  };
  return result;
};

// src/rules/sql-032-explain-analyze-destructive.ts
var DESTRUCTIVE_INNER = /* @__PURE__ */ new Set(["InsertStmt", "UpdateStmt", "DeleteStmt", "MergeStmt"]);
var SQL_032 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["ExplainStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("ExplainStmt" in obj)) return void 0;
    const stmt = obj["ExplainStmt"];
    const opts = stmt["options"];
    if (!Array.isArray(opts)) return void 0;
    let hasAnalyze = false;
    for (const o of opts) {
      if (!o || typeof o !== "object") continue;
      const de = o["DefElem"];
      if (!de || typeof de !== "object") continue;
      if (de["defname"] === "analyze") {
        hasAnalyze = true;
        break;
      }
    }
    if (!hasAnalyze) return void 0;
    const q = stmt["query"];
    if (!q || typeof q !== "object") return void 0;
    for (const k of Object.keys(q)) {
      if (DESTRUCTIVE_INNER.has(k)) {
        fired = k;
        return "stop";
      }
    }
    return void 0;
  });
  if (!fired) return null;
  const innerKind = fired;
  const verb = innerKind === "InsertStmt" ? "INSERT" : innerKind === "UpdateStmt" ? "UPDATE" : innerKind === "DeleteStmt" ? "DELETE" : "MERGE";
  const result = {
    code: "SQL-032",
    title: `EXPLAIN ANALYZE ${verb} \u2014 really executes the statement`,
    severity: "info",
    confidence: 80,
    detail: `EXPLAIN ANALYZE actually runs the statement to measure real timing \u2014 \`EXPLAIN ANALYZE ${verb} \u2026\` really performs the ${verb}. Many operators read "EXPLAIN" and assume read-only; the ANALYZE keyword acts as a silent destructiveness modifier.`,
    fix: `If you wanted a read-only plan, drop the ANALYZE keyword (\`EXPLAIN ${verb} \u2026\`). If you genuinely need timing data for a destructive statement, wrap it in an explicit transaction with ROLLBACK so the changes do not persist: \`BEGIN; EXPLAIN ANALYZE ${verb} \u2026; ROLLBACK;\`.`,
    threatCategories: ["destruction"]
  };
  return result;
};

// src/rules/sql-033-do-block.ts
var SQL_033 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["DoStmt"]))) return null;
  let fired = false;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if ("DoStmt" in obj) {
      fired = true;
      return "stop";
    }
    return void 0;
  });
  if (!fired) return null;
  const result = {
    code: "SQL-033",
    title: "DO $$ \u2026 $$ \u2014 opaque procedural block",
    severity: "info",
    confidence: 70,
    detail: "A DO block hands an opaque string to a procedural language (default plpgsql) and executes it at the server. From a static-analysis perspective the body is not visible \u2014 VibeGuard cannot tell whether it contains DROP TABLE, PERFORM dblink_connect(\u2026), or anything else. The agent has asked the database to run code we cannot inspect.",
    fix: "If the DO body is a one-off migration step, lift its statements out of the DO block so each is independently visible to static analysis. If the block must remain (e.g. it uses control flow), document what it does and ensure an operator review of the body before merge.",
    threatCategories: ["injection"]
  };
  return result;
};

// src/rules/sql-034-literal-tautology.ts
var SQL_034 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["UpdateStmt", "DeleteStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    let whereClause = void 0;
    let verb2 = null;
    if ("UpdateStmt" in obj) {
      const stmt = obj["UpdateStmt"];
      whereClause = stmt["whereClause"];
      verb2 = "UPDATE";
    } else if ("DeleteStmt" in obj) {
      const stmt = obj["DeleteStmt"];
      whereClause = stmt["whereClause"];
      verb2 = "DELETE";
    } else {
      return void 0;
    }
    if (whereClause === void 0 || whereClause === null) return void 0;
    if (!isLiteralTautology(whereClause)) return void 0;
    fired = verb2;
    return "stop";
  });
  if (!fired) return null;
  const verb = fired;
  const result = {
    code: "SQL-034",
    title: `${verb} WHERE <tautology> \u2014 full-table mutation in disguise`,
    severity: "block",
    confidence: 95,
    detail: `The WHERE clause on this ${verb} reduces to a literal tautology (\`1=1\`, \`true\`, \`'a'='a'\`, \`id = id\`, or similar). It evaluates TRUE without reading any column value, so the ${verb} affects every row in the target table \u2014 a full-table mutation expressed in syntax that satisfies the "must have a WHERE" check.`,
    fix: `Replace the tautology with a WHERE clause that identifies the rows you intend to mutate (e.g. \`WHERE id = ?\`, \`WHERE updated_at < now() - interval '7 days'\`). If you genuinely intend a full-table mutation, route it through an explicit migration step that operators can review.`,
    threatCategories: ["destruction"]
  };
  return result;
};

// src/rules/from-join-helpers.ts
function rangeVarName(rv) {
  if (!rv || typeof rv !== "object") return void 0;
  const inner = rv["RangeVar"] ?? rv;
  if (!inner || typeof inner !== "object") return void 0;
  const o = inner;
  const alias = o["alias"];
  if (alias && typeof alias === "object") {
    const a = alias["aliasname"];
    if (typeof a === "string") return a;
  }
  const rn = o["relname"];
  return typeof rn === "string" ? rn : void 0;
}
function walkSubtree(node, visit) {
  if (!node) return false;
  if (Array.isArray(node)) {
    for (const c of node) {
      if (walkSubtree(c, visit)) return true;
    }
    return false;
  }
  if (typeof node !== "object") return false;
  if (visit(node) === "stop") return true;
  for (const v of Object.values(node)) {
    if (walkSubtree(v, visit)) return true;
  }
  return false;
}
function whereHasQualifierIn(whereNode, names) {
  if (names.size === 0) return false;
  let found = false;
  walkSubtree(whereNode, (n) => {
    const cr = n["ColumnRef"];
    if (!cr || typeof cr !== "object") return void 0;
    const fields = cr["fields"];
    if (!Array.isArray(fields) || fields.length < 2) return void 0;
    const f0 = fields[0];
    if (!f0 || typeof f0 !== "object") return void 0;
    const s = f0["String"];
    if (!s || typeof s !== "object") return void 0;
    const sval = s["sval"];
    if (typeof sval !== "string") return void 0;
    if (names.has(sval)) {
      found = true;
      return "stop";
    }
    return void 0;
  });
  return found;
}

// src/rules/sql-035-update-from-no-join.ts
var SQL_035 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["UpdateStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("UpdateStmt" in obj)) return void 0;
    const stmt = obj["UpdateStmt"];
    const fromClause = stmt["fromClause"];
    if (!Array.isArray(fromClause) || fromClause.length === 0) return void 0;
    const fromNames = /* @__PURE__ */ new Set();
    for (const fr of fromClause) {
      const n = rangeVarName(fr);
      if (n) fromNames.add(n);
    }
    const whereClause = stmt["whereClause"];
    if (whereClause === void 0 || whereClause === null) {
      const rel = stmt["relation"];
      fired = typeof rel?.["relname"] === "string" ? rel["relname"] : "<unknown>";
      return "stop";
    }
    if (!whereHasQualifierIn(whereClause, fromNames)) {
      const rel = stmt["relation"];
      fired = typeof rel?.["relname"] === "string" ? rel["relname"] : "<unknown>";
      return "stop";
    }
    return void 0;
  });
  if (!fired) return null;
  const target = fired;
  const result = {
    code: "SQL-035",
    title: "UPDATE \u2026 FROM without join predicate \u2014 silent cross-join overwrite",
    severity: "block",
    confidence: 90,
    detail: `UPDATE \`${target}\` \u2026 FROM <source> with no WHERE qualifier on the source side takes the Cartesian product of the source rows and updates the target for each pair. Typically every target row is updated at least once, with the column value pulled from an arbitrary source row. The damage is silent: the row count returned is large but plausible, and the data is randomized.`,
    fix: `Add a join predicate that ties target rows to source rows (e.g. \`WHERE ${target}.id = <source>.target_id\`). If the source genuinely has one row and the cross-join is intended, document it \u2014 the operator should be able to confirm the row count is bounded.`,
    threatCategories: ["destruction"]
  };
  return result;
};

// src/rules/sql-036-delete-using-no-join.ts
var SQL_036 = (ast) => {
  if (!hasTopLevelStmt(ast, /* @__PURE__ */ new Set(["DeleteStmt"]))) return null;
  let fired = null;
  astWalk(ast, (node) => {
    if (fired) return "stop";
    if (!node || typeof node !== "object") return void 0;
    const obj = node;
    if (!("DeleteStmt" in obj)) return void 0;
    const stmt = obj["DeleteStmt"];
    const usingClause = stmt["usingClause"];
    if (!Array.isArray(usingClause) || usingClause.length === 0) return void 0;
    const usingNames = /* @__PURE__ */ new Set();
    for (const u of usingClause) {
      const n = rangeVarName(u);
      if (n) usingNames.add(n);
    }
    const whereClause = stmt["whereClause"];
    if (whereClause === void 0 || whereClause === null) {
      const rel = stmt["relation"];
      fired = typeof rel?.["relname"] === "string" ? rel["relname"] : "<unknown>";
      return "stop";
    }
    if (!whereHasQualifierIn(whereClause, usingNames)) {
      const rel = stmt["relation"];
      fired = typeof rel?.["relname"] === "string" ? rel["relname"] : "<unknown>";
      return "stop";
    }
    return void 0;
  });
  if (!fired) return null;
  const target = fired;
  const result = {
    code: "SQL-036",
    title: "DELETE \u2026 USING without join predicate \u2014 silent cross-join wipe",
    severity: "block",
    confidence: 90,
    detail: `DELETE FROM \`${target}\` USING <source> with no WHERE qualifier on the source side takes the Cartesian product of the USING rows and deletes every target row that has any source-row pairing \u2014 usually every row in \`${target}\`. SQL-003 catches the no-USING variant; this rule catches the USING-side blind spot.`,
    fix: `Add a join predicate that ties target rows to source rows (e.g. \`WHERE ${target}.id = <source>.target_id\`). If you genuinely intend to delete every row in \`${target}\` based on a source-side condition, route it through an explicit DELETE with that condition expressed in the WHERE clause.`,
    threatCategories: ["destruction"]
  };
  return result;
};

// src/rules/index.ts
var RULE_REGISTRY = [
  { code: "SQL-001", rule: SQL_001, defaultEnabled: true, fixer: SQL_001_FIX },
  { code: "SQL-002", rule: SQL_002, defaultEnabled: true },
  { code: "SQL-003", rule: SQL_003, defaultEnabled: true },
  { code: "SQL-004", rule: SQL_004, defaultEnabled: true },
  { code: "SQL-005", rule: SQL_005, defaultEnabled: true, fixer: SQL_005_FIX },
  { code: "SQL-006", rule: SQL_006, defaultEnabled: true, fixer: SQL_006_FIX },
  { code: "SQL-007", rule: SQL_007, defaultEnabled: true },
  { code: "SQL-008", rule: SQL_008, defaultEnabled: true, confidenceRange: [75, 90] },
  { code: "SQL-009", rule: SQL_009, defaultEnabled: true },
  { code: "SQL-010", rule: SQL_010, defaultEnabled: true },
  { code: "SQL-011", rule: SQL_011, defaultEnabled: true, fixer: SQL_011_FIX },
  { code: "SQL-012", rule: SQL_012, defaultEnabled: true },
  { code: "SQL-013", rule: SQL_013, defaultEnabled: true },
  { code: "SQL-014", rule: SQL_014, defaultEnabled: false },
  // OPT-IN
  { code: "SQL-015", rule: SQL_015, defaultEnabled: true },
  { code: "SQL-016", rule: SQL_016, defaultEnabled: true },
  { code: "SQL-017", rule: SQL_017, defaultEnabled: true },
  { code: "SQL-018", rule: SQL_018, defaultEnabled: true },
  { code: "SQL-019", rule: SQL_019, defaultEnabled: true },
  { code: "SQL-020", rule: SQL_020, defaultEnabled: true },
  { code: "SQL-021", rule: SQL_021, defaultEnabled: true },
  { code: "SQL-022", rule: SQL_022, defaultEnabled: true },
  { code: "SQL-023", rule: SQL_023, defaultEnabled: true },
  { code: "SQL-024", rule: SQL_024, defaultEnabled: true },
  { code: "SQL-025", rule: SQL_025, defaultEnabled: true },
  { code: "SQL-026", rule: SQL_026, defaultEnabled: true },
  { code: "SQL-027", rule: SQL_027, defaultEnabled: true },
  { code: "SQL-028", rule: SQL_028, defaultEnabled: true },
  { code: "SQL-029", rule: SQL_029, defaultEnabled: true },
  { code: "SQL-030", rule: SQL_030, defaultEnabled: true },
  { code: "SQL-031", rule: SQL_031, defaultEnabled: true },
  { code: "SQL-032", rule: SQL_032, defaultEnabled: true },
  { code: "SQL-033", rule: SQL_033, defaultEnabled: true },
  { code: "SQL-034", rule: SQL_034, defaultEnabled: true },
  { code: "SQL-035", rule: SQL_035, defaultEnabled: true },
  { code: "SQL-036", rule: SQL_036, defaultEnabled: true }
];
var RULES = RULE_REGISTRY.filter((entry) => entry.defaultEnabled).map((entry) => entry.rule);
export {
  RULES,
  RULE_REGISTRY,
  runRules
};
//# sourceMappingURL=rules-export.js.map